import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react'
import { CampaignModal } from './components/CampaignModal'
import { ActionTooltip } from './components/ActionTooltip'
import { ConfirmDialog } from './components/ConfirmDialog'
import { TaskPanel } from './components/TaskPanel'
import { Brand } from './components/Brand'
import { Icon } from './components/Icon'
import { Layout } from './components/Layout'
import { Reports } from './components/Reports'
import { GoogleWorkspaceLogin, loadGoogleIdentityServices, requestGoogleProfileToken } from './components/GoogleWorkspaceLogin'
import { ClientManagementModal } from './components/ClientManagementModal'
import { ClientLogoField } from './components/ClientLogoField'
import { UserAccessSettings } from './components/UserAccessSettings'
import { UserAvatar } from './components/UserAvatar'
import { TalentDesk } from './components/TalentDesk'
import { LoginWebGLBackdrop } from './components/LoginWebGLBackdrop'
import type { AuthenticatedUser } from './auth'
import { campaignIsVisible, clientForAccess } from './accessScope'
import { canCreateCampaign as canCreateCampaignForProfile, hasFullCampaignClientAccess } from './campaignCreation'
import { initialUserAccessScopes, initialUserLevels, initialUserProfiles, userProfileOptions, users, type UserKey } from './data'
import { completedDependents, dependencyState } from './taskDependencies'
import { canCreateTaskInCampaign } from './taskAccess'
import { approveCampaignReviewSubtasks, campaignReviewReopenTransition, campaignReviewTransition, canMoveCampaignToReview, canReviewCampaign, canWithdrawCampaignTaskApproval, withdrawCampaignReviewSubtaskApproval } from './campaignReview'
import { campaignStatusActivityDescription, campaignStatusLabel } from './campaignStatus'
import { applyCampaignAlteration, createCampaignAlteration, resolveSubtaskAlterations, type RequestedCampaignAlteration } from './campaignAlterations'
import { canDeleteCampaignTask, canEditAssignedCampaignDetails, canEditCampaignPanel } from './campaignPermissions'
import { hasUnrestrictedStatusAccess } from './statusPermissions'
import { acknowledgeTaskNotifications, createAssignmentNotification, createCampaignAlterationNotification, createNewCampaignAssignmentNotification, unreadTaskNotificationIds } from './taskNotifications'
import { parseTaskDeadline, toLocalDateTimeValue } from './taskDeadlines'
import { normalizeSubtaskVolume } from './subtaskVolume'
import { canEditJournalPages, normalizeJournalPages } from './journalPages'
import { expandCollaboratorInitials } from './collaboratorNames'
import { normalizeTaskSupportUsers } from './taskSupport'
import { createSubtaskDuplicate } from './subtaskDuplication'
import { stopSubtaskTimers, subtaskTimeEntries, toggleUserSubtaskTimer, userIsTimingSubtask } from './subtaskTime'
import { dragGlowStatus, reorderCampaignTask, type TaskDropPlacement } from './taskOrdering'
import { userLevelOptions, type Campaign, type Client, type MaterialCategory, type Page, type RegisteredAccount, type ReportArea, type Role, type Subtask, type SubtaskKind, type SystemNotification, type Task, type TaskActivityEntry, type TaskStatus, type UserAccessScope, type UserAccessScopes, type UserLevel, type UserProfile, type WorkCategory } from './types'
import { type PlatformActivity, type WorkspaceSnapshot } from './workspaceStorage'
import { loadRemoteWorkspace, mutateRemoteWorkspace, WorkspaceApiError, type RemoteWorkspace } from './workspaceApi'
import { canAccessTalentDesk } from './talentDesk'
import { defaultSubtaskPresentation } from './materials'

type Modal = 'campaign' | 'client' | null

interface Confirmation {
  title: string
  description: string
  confirmLabel: string
  cancelLabel?: string
  tone?: 'danger' | 'warning'
  onConfirm: () => void
}

export const pagePaths: Record<Page, string> = {
  dashboard: '/dashboard',
  campaigns: '/campaigns/list',
  tasks: '/campaigns',
  reports: '/reports',
  talentDesk: '/talent-desk',
  settings: '/settings',
}

function normalizedPath(pathname: string) {
  return pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname
}

export function campaignPath(campaignId: string) {
  return `/campaigns/${encodeURIComponent(campaignId)}`
}

export function campaignIdFromPath(pathname = window.location.pathname) {
  const match = normalizedPath(pathname).match(/^\/campaigns\/([^/]+)$/)
  if (!match || match[1] === 'list') return undefined
  try {
    return decodeURIComponent(match[1])
  } catch {
    return undefined
  }
}

export function isKnownApplicationPath(pathname = window.location.pathname) {
  const currentPath = normalizedPath(pathname)
  return currentPath === '/tasks' || Boolean(campaignIdFromPath(currentPath)) || Object.values(pagePaths).includes(currentPath)
}

const categoryByArea: Record<ReportArea, WorkCategory> = {
  'Mídia e Promoção': 'Diagramação', Digital: 'Diagramação', 'Branding Institucional': 'Criação/Redação',
  'Gestão de Demandas': 'Gestão/Planejamento', Farmácia: 'Criação/Redação', 'Marcas Próprias': 'Criação/Redação',
  Trade: 'Criação/Redação', Patrocínios: 'Gestão/Planejamento', 'Comunicação Visual': 'Diagramação',
  'Comunicação Regional': 'Criação/Redação',
}

export function pageFromPath(pathname = window.location.pathname): Page {
  const currentPath = normalizedPath(pathname)
  if (currentPath === '/tasks' || campaignIdFromPath(currentPath)) return 'tasks'
  return (Object.entries(pagePaths).find(([, path]) => path === currentPath)?.[0] as Page | undefined) ?? 'dashboard'
}

function PageHead({ title, subtitle, action, actionShortcut, onAction }: { title: string; subtitle: string; action?: string; actionShortcut?: string; onAction?: () => void }) {
  const visibleAction = title.startsWith('Olá,') ? undefined : action
  return <div className="page-head"><div><h1>{title}</h1><p>{subtitle}</p></div>{visibleAction && <button className={`primary ${visibleAction === 'Nova campanha' ? 'new-campaign-action' : ''}`} aria-keyshortcuts={actionShortcut} title={actionShortcut ? `Atalho: ${actionShortcut}` : undefined} onClick={onAction}><Icon name="plus"/>{visibleAction}{actionShortcut && <kbd aria-hidden="true">{actionShortcut}</kbd>}</button>}</div>
}

export function isNewCampaignShortcut(event: Pick<KeyboardEvent, 'key' | 'repeat' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'target'>) {
  const target = event.target as Element | null
  const editingText = typeof target?.closest === 'function' && Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]'))
  return event.key.toLowerCase() === 'n' && !event.repeat && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && !editingText
}

export function dashboardPriorityTasks(tasks: Task[], currentUser: string, limit = 5, now = new Date()) {
  const priorityRank: Record<Task['priority'], number> = { Alta: 0, Média: 1, Baixa: 2 }
  return tasks
    .filter(task => task.status !== 'Aprovada' && task.status !== 'Reprovada')
    .map((task, index) => ({ task, index }))
    .sort((left, right) => {
      const responsibilityDifference = Number(right.task.owner === currentUser) - Number(left.task.owner === currentUser)
      if (responsibilityDifference) return responsibilityDifference
      const leftDeadline = parseTaskDeadline(left.task.due, now)?.getTime() ?? Number.POSITIVE_INFINITY
      const rightDeadline = parseTaskDeadline(right.task.due, now)?.getTime() ?? Number.POSITIVE_INFINITY
      return leftDeadline - rightDeadline || priorityRank[left.task.priority] - priorityRank[right.task.priority] || left.index - right.index
    })
    .slice(0, limit)
    .map(item => item.task)
}

function formatTime(seconds: number) {
  const h = Math.floor(seconds / 3600).toString().padStart(2, '0')
  const m = Math.floor(seconds % 3600 / 60).toString().padStart(2, '0')
  const s = Math.floor(seconds % 60).toString().padStart(2, '0')
  return `${h}:${m}:${s}`
}

function activityTimestamp(date = new Date()) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date).replace('.', '')
}

function createActivityEntry(user: string, status: TaskStatus, description: string): TaskActivityEntry {
  const occurredOn = new Date()
  return {
    id: crypto.randomUUID(),
    user,
    status,
    description,
    occurredAt: activityTimestamp(occurredOn),
    occurredOn: occurredOn.toISOString(),
  }
}

type WorkspaceSliceSetter<Key extends keyof WorkspaceSnapshot> = Dispatch<SetStateAction<WorkspaceSnapshot[Key]>>

export function restoreWorkspaceWithoutRewindingTime(previous: WorkspaceSnapshot, current: WorkspaceSnapshot): WorkspaceSnapshot {
  const currentTasks = new Map(current.tasks.map(task => [task.id, task]))
  return {
    ...previous,
    tasks: previous.tasks.map(previousTask => {
      const currentTask = currentTasks.get(previousTask.id)
      if (!currentTask) return previousTask
      const currentSubtasks = new Map(currentTask.subtasks.map(subtask => [subtask.id, subtask]))
      let subtaskTimingChanged = false
      const subtasks = previousTask.subtasks.map(previousSubtask => {
        const currentSubtask = currentSubtasks.get(previousSubtask.id)
        if (!currentSubtask) return previousSubtask
        const timingChanged = previousSubtask.seconds !== currentSubtask.seconds || previousSubtask.running !== currentSubtask.running || JSON.stringify(previousSubtask.timeEntries ?? []) !== JSON.stringify(currentSubtask.timeEntries ?? [])
        subtaskTimingChanged ||= timingChanged
        return {
          ...previousSubtask,
          status: timingChanged ? currentSubtask.status : previousSubtask.status,
          seconds: currentSubtask.seconds,
          running: currentSubtask.running,
          timeEntries: currentSubtask.timeEntries,
        }
      })
      const taskTimingChanged = previousTask.seconds !== currentTask.seconds || previousTask.running !== currentTask.running || JSON.stringify(previousTask.timeEntries) !== JSON.stringify(currentTask.timeEntries)
      const previousActivityIds = new Set(previousTask.activityEntries.map(entry => entry.id))
      const laterTimingActivities = currentTask.activityEntries.filter(entry => !previousActivityIds.has(entry.id) && (entry.description.includes('cronômetro') || entry.description.includes('dar play')))
      return {
        ...previousTask,
        status: taskTimingChanged || subtaskTimingChanged ? currentTask.status : previousTask.status,
        seconds: currentTask.seconds,
        running: currentTask.running,
        runningUser: currentTask.runningUser,
        activeTimeEntryId: currentTask.activeTimeEntryId,
        timeEntries: currentTask.timeEntries,
        activityEntries: [...previousTask.activityEntries, ...laterTimingActivities],
        subtasks,
      }
    }),
  }
}

function useWorkspaceState(initialWorkspace: WorkspaceSnapshot) {
  const [workspace, setWorkspaceState] = useState(initialWorkspace)
  const history = useRef<WorkspaceSnapshot[]>([])
  const historyTransactionOpen = useRef(false)
  const historyTransactionTimer = useRef<number | undefined>(undefined)

  const recordHistory = useCallback((snapshot: WorkspaceSnapshot) => {
    if (historyTransactionOpen.current) return
    history.current = [...history.current.slice(-49), structuredClone(snapshot)]
    historyTransactionOpen.current = true
    window.clearTimeout(historyTransactionTimer.current)
    historyTransactionTimer.current = window.setTimeout(() => { historyTransactionOpen.current = false }, 0)
  }, [])

  const workspaceSetter = useCallback((value: SetStateAction<WorkspaceSnapshot>, record = true) => {
    setWorkspaceState(current => {
      const next = typeof value === 'function' ? (value as (currentValue: WorkspaceSnapshot) => WorkspaceSnapshot)(current) : value
      if (Object.is(next, current)) return current
      if (record) recordHistory(current)
      return next
    })
  }, [recordHistory])

  const sliceSetter = useCallback(<Key extends keyof WorkspaceSnapshot>(key: Key, record = true): WorkspaceSliceSetter<Key> => value => {
    workspaceSetter(current => {
      const nextValue = typeof value === 'function'
        ? (value as (currentValue: WorkspaceSnapshot[Key]) => WorkspaceSnapshot[Key])(current[key])
        : value
      if (Object.is(nextValue, current[key])) return current
      return { ...current, [key]: nextValue }
    }, record)
  }, [workspaceSetter])

  const setters = useMemo(() => ({
    setClients: sliceSetter('clients'),
    setCampaigns: sliceSetter('campaigns'),
    setTasks: sliceSetter('tasks'),
    setTasksTransient: sliceSetter('tasks', false),
    setPlatformActivities: sliceSetter('platformActivities'),
    setNotifications: sliceSetter('notifications'),
    setUserProfiles: sliceSetter('userProfiles'),
    setUserLevels: sliceSetter('userLevels'),
    setUserAccessScopes: sliceSetter('userAccessScopes'),
    setRegisteredAccounts: sliceSetter('registeredAccounts'),
    setRegisteredAccountsTransient: sliceSetter('registeredAccounts', false),
  }), [sliceSetter])

  const undo = useCallback(() => {
    const previous = history.current.pop()
    if (!previous) return false
    window.clearTimeout(historyTransactionTimer.current)
    historyTransactionOpen.current = false
    setWorkspaceState(current => restoreWorkspaceWithoutRewindingTime(previous, current))
    return true
  }, [])

  const clearHistory = useCallback(() => {
    history.current = []
    window.clearTimeout(historyTransactionTimer.current)
    historyTransactionOpen.current = false
  }, [])

  return { workspace, setWorkspace: workspaceSetter, undo, clearHistory, ...setters }
}

function statusChangeActivity(user: string, entity: 'task' | 'deliverable', entityId: string, entityTitle: string, from: TaskStatus, to: TaskStatus, customDescription?: string): TaskActivityEntry {
  const occurredOn = new Date()
  return {
    id: crypto.randomUUID(),
    user,
    status: to,
    description: customDescription ?? (entity === 'task' ? `alterou o status da campanha de ${campaignStatusLabel(from)} para ${campaignStatusLabel(to)}` : `moveu a entrega “${entityTitle}” de ${from} para ${to}`),
    occurredAt: activityTimestamp(occurredOn),
    occurredOn: occurredOn.toISOString(),
    statusChange: { entity, entityId, entityTitle, from, to },
  }
}

function Login({ onAuthenticated }: { onAuthenticated: (user: AuthenticatedUser) => void }) {
  return <main className="login-page">
    <section className="login-panel">
      <header className="login-header">
        <div className="login-product" aria-label="StarTracker Workspace">
          <span className="login-product-mark"><img src="/brand/startracker-telescope.png" alt=""/></span>
          <span className="login-product-copy"><b>StarTracker</b><small>AMBIENTE INTEGRADO</small></span>
        </div>
        <div className="login-starmkt-signature"><span>Uma solução</span><a href="https://starmkt.com.br/" target="_blank" rel="noreferrer" aria-label="Abrir o site da STARMKT em uma nova guia"><img src="/brand/logo-star-black.png" alt="STARMKT"/></a></div>
      </header>
      <div className="login-box">
        <span className="login-kicker"><i aria-hidden="true">⭑</i> Seu espaço de trabalho</span>
        <h1>Bom te ver<br/>por aqui.</h1>
        <p className="subtle">Entre para acompanhar campanhas, organizar entregas e manter o time no mesmo ritmo.</p>
        <GoogleWorkspaceLogin onAuthenticated={onAuthenticated}/>
        <div className="workspace-note"><span className="workspace-shield"><Icon name="shield"/></span><span><b>Acesso corporativo protegido</b>Use sua conta <strong>@starmkt.com.br</strong>. Sua identidade é validada com segurança pelo Google.</span></div>
      </div>
      <footer className="login-footer"><span>© 2026 STARMKT</span><a href="https://starmkt.com.br/" target="_blank" rel="noreferrer" aria-label="Abrir o site da STARMKT em uma nova guia"><img src="/brand/logo-star-black.png" alt="STARMKT"/></a><span>StarTracker v0.2</span></footer>
    </section>
    <section className="login-visual">
      <LoginWebGLBackdrop/>
      <div className="login-prismatic-glass" aria-hidden="true"/>
      <header className="visual-header"><span>Feito para atender todo processo</span><a href="https://starmkt.com.br/" target="_blank" rel="noreferrer" aria-label="Abrir o site da STARMKT em uma nova guia"><img src="/brand/logo-star-white.png" alt="STARMKT"/></a></header>
      <div className="visual-copy">
        <span className="visual-kicker">Do briefing à entrega</span>
        <h2>Mais clareza no caminho.<br/><em>Mais tempo para criar.</em></h2>
        <p>Times, campanhas e prazos reunidos em um fluxo simples de acompanhar.</p>
      </div>
      <div className="workflow-preview" aria-label="Fluxo de trabalho no StarTracker">
        <div className="workflow-preview-head"><div><span className="workflow-live"/>Gestão KANBAN, BI & Relatórios</div><small>Sistema ERP</small></div>
        <div className="workflow-step active"><span>01</span><div><b>Crie campanhas</b><small>Briefing, prioridades e responsáveis</small></div><em>Agora</em></div>
        <div className="workflow-connector"><i/></div>
        <div className="workflow-step"><span>02</span><div><b>Rastreie tarefas e entregas</b><small>Entregas visíveis para todo o time</small></div><em>Gestão e controle</em></div>
        <div className="workflow-connector"><i/></div>
        <div className="workflow-step"><span>03</span><div><b>Acompanhe e atualize o desempenho profissional</b><small>Tempo, prazos e resultados em um só lugar</small></div><em>Relatórios & Business Intelligence</em></div>
      </div>
      <footer className="visual-footer"><div className="visual-team"><span>TM</span><span>AM</span><span>WS</span></div><p><b>Integração com Google Workspace corporativo.</b></p></footer>
    </section>
  </main>
}

export default function App() {
  const [initialWorkspace] = useState<WorkspaceSnapshot>(() => ({
    clients: [],
    campaigns: [],
    tasks: [],
    platformActivities: [],
    notifications: [],
    userProfiles: initialUserProfiles,
    userLevels: initialUserLevels,
    userAccessScopes: initialUserAccessScopes,
    registeredAccounts: [],
  }))
  const [authenticated, setAuthenticated] = useState(false)
  const [authenticationLoading, setAuthenticationLoading] = useState(true)
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(null)
  const [workspaceReady, setWorkspaceReady] = useState(false)
  const [workspaceError, setWorkspaceError] = useState('')
  const [role, setRoleState] = useState<Role>('worker')
  const [simulatedUserKey, setSimulatedUserKey] = useState<UserKey | null>(null)
  const [page, setPageState] = useState<Page>(pageFromPath)
  const [modal, setModal] = useState<Modal>(null)
  const {
    workspace, setWorkspace, undo, clearHistory, setClients, setCampaigns, setTasks, setTasksTransient, setPlatformActivities,
    setNotifications, setUserProfiles, setUserLevels, setUserAccessScopes, setRegisteredAccounts,
  } = useWorkspaceState(initialWorkspace)
  const { clients, campaigns, tasks, platformActivities, notifications, userProfiles, userLevels, userAccessScopes, registeredAccounts } = workspace
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null)
  const [kanbanAnchor, setKanbanAnchor] = useState<{ taskId: string; requestId: number } | null>(null)
  const [priorityHighlightedTaskIds, setPriorityHighlightedTaskIds] = useState<string[]>([])
  const [searchedAccountEmail, setSearchedAccountEmail] = useState<string | null>(null)
  const [editingClientId, setEditingClientId] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const [toast, setToast] = useState('')
  const [avatarSyncing, setAvatarSyncing] = useState(false)
  const authenticatedAccount = authenticatedUser ? registeredAccounts.find(account => account.email === authenticatedUser.email.toLowerCase()) : undefined
  const simulatedUser = simulatedUserKey ? users[simulatedUserKey] : undefined
  const roleFallbackUser = users[role]
  const currentUserInitials = simulatedUser?.initials ?? authenticatedUser?.initials ?? authenticatedAccount?.initials ?? roleFallbackUser.initials
  const currentUserName = simulatedUser?.name ?? authenticatedAccount?.name ?? authenticatedUser?.name ?? roleFallbackUser.name
  const currentUserProfile = simulatedUserKey ? userProfiles[simulatedUserKey] : authenticatedAccount?.profile ?? (role === 'admin' ? 'Administrador' : 'Diagramador')
  const canCreateCampaign = canCreateCampaignForProfile(role, currentUserProfile)
  const canOverrideStatus = hasUnrestrictedStatusAccess(currentUserProfile)
  const activeAccessScope = simulatedUserKey ? userAccessScopes[simulatedUserKey] : authenticatedUser?.accessScope ?? authenticatedAccount?.accessScope ?? { allClients: false, clients: {} }
  const activeAccessScopeKey = JSON.stringify(activeAccessScope)
  const registeredEmails = new Set(registeredAccounts.map(account => account.email))
  const accountDirectory: RegisteredAccount[] = [
    ...(import.meta.env.DEV && authenticatedUser?.systemRole === 'admin' ? Object.entries(users) : []).flatMap(([accountRole, user]) => registeredEmails.has(user.email.toLowerCase()) ? [] : [{
      email: user.email.toLowerCase(), name: user.name, initials: user.initials,
      profile: userProfiles[accountRole], level: userLevels[accountRole], accessScope: userAccessScopes[accountRole], lastLoginAt: '', systemRole: user.systemRole,
    }]),
    ...registeredAccounts.map(account => account.email === authenticatedUser?.email.toLowerCase() && (authenticatedUser.picture || authenticatedUser.avatarUrl)
      ? { ...account, picture: authenticatedUser.picture ?? authenticatedUser.avatarUrl }
      : account),
  ]
  const collaboratorName = (initials: string) => accountDirectory.find(account => account.initials === initials)?.name ?? initials
  const workspaceSnapshotRef = useRef<WorkspaceSnapshot>(workspace)
  const persistedWorkspaceRef = useRef('')
  const persistedRemoteWorkspaceRef = useRef<RemoteWorkspace | null>(null)
  const workspaceSaveTimerRef = useRef<number | undefined>(undefined)
  const workspaceSaveInFlightRef = useRef(false)
  workspaceSnapshotRef.current = workspace

  const finishAuthentication = useCallback((user: AuthenticatedUser) => {
    setAuthenticatedUser(user)
    setRoleState(user.systemRole)
    setSimulatedUserKey(null)
    setAuthenticated(true)
    setAuthenticationLoading(false)
  }, [])

  useEffect(() => {
    let active = true
    fetch('/api/auth/session', { credentials: 'include', cache: 'no-store' })
      .then(async response => {
        const result = await response.json() as { authenticated?: boolean; user?: AuthenticatedUser }
        if (active && response.ok && result.authenticated && result.user) finishAuthentication(result.user)
        else if (active) setAuthenticationLoading(false)
      })
      .catch(() => { if (active) setAuthenticationLoading(false) })
    return () => { active = false }
  }, [finishAuthentication])

  useEffect(() => {
    if (authenticated) void loadGoogleIdentityServices()
  }, [authenticated])

  useEffect(() => {
    const timer = window.setInterval(() => setTasksTransient(current => current.map(task => {
      const hasRunningSubtask = task.subtasks.some(subtask => subtaskTimeEntries(subtask).some(entry => entry.running))
      if (!hasRunningSubtask) return task
      return {
        ...task,
        subtasks: task.subtasks.map(subtask => {
          const timeEntries = subtaskTimeEntries(subtask)
          const runningEntries = timeEntries.filter(entry => entry.running).length
          if (!runningEntries) return subtask.running ? { ...subtask, running: false, timeEntries } : subtask
          return {
            ...subtask,
            seconds: subtask.seconds + runningEntries,
            running: true,
            timeEntries: timeEntries.map(entry => entry.running ? { ...entry, seconds: entry.seconds + 1 } : entry),
          }
        }),
      }
    })), 1000)
    return () => window.clearInterval(timer)
  }, [setTasksTransient])

  useEffect(() => {
    function handleUndoShortcut(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'z') return
      const target = event.target as Element | null
      if (target?.closest('input, textarea, [contenteditable="true"], [role="textbox"]')) return
      event.preventDefault()
      setToast(undo() ? 'Última ação desfeita.' : 'Não há ações para desfazer.')
    }
    window.addEventListener('keydown', handleUndoShortcut)
    return () => window.removeEventListener('keydown', handleUndoShortcut)
  }, [undo])

  useEffect(() => {
    if (!authenticatedUser) return
    let active = true
    setWorkspaceReady(false)
    setWorkspaceError('')
    persistedRemoteWorkspaceRef.current = null
    loadRemoteWorkspace().then(remote => {
      if (!active) return
      persistedWorkspaceRef.current = JSON.stringify(remote.snapshot)
      persistedRemoteWorkspaceRef.current = remote
      setWorkspace(remote.snapshot, false)
      clearHistory()
      setWorkspaceReady(true)
    }).catch(error => {
      if (!active) return
      setWorkspaceError(error instanceof Error ? error.message : 'Não foi possível carregar os dados do StarTracker.')
    })
    return () => { active = false }
  }, [authenticatedUser?.sub, clearHistory, setWorkspace])

  useEffect(() => {
    if (!workspaceReady) return
    const serialized = JSON.stringify(workspace)
    if (serialized === persistedWorkspaceRef.current) return
    window.clearTimeout(workspaceSaveTimerRef.current)

    async function persistLatestWorkspace() {
      if (workspaceSaveInFlightRef.current) {
        workspaceSaveTimerRef.current = window.setTimeout(() => void persistLatestWorkspace(), 250)
        return
      }
      const snapshot = workspaceSnapshotRef.current
      const snapshotSerialized = JSON.stringify(snapshot)
      if (snapshotSerialized === persistedWorkspaceRef.current) return
      const base = persistedRemoteWorkspaceRef.current
      if (!base) return
      workspaceSaveInFlightRef.current = true
      try {
        const remote = await mutateRemoteWorkspace(base, snapshot)
        persistedWorkspaceRef.current = JSON.stringify(remote.snapshot)
        persistedRemoteWorkspaceRef.current = remote
        if (JSON.stringify(workspaceSnapshotRef.current) === snapshotSerialized && JSON.stringify(remote.snapshot) !== snapshotSerialized) {
          setWorkspace(remote.snapshot, false)
        }
      } catch (error) {
        if (error instanceof WorkspaceApiError && error.status === 409) {
          const remote = await loadRemoteWorkspace()
          persistedWorkspaceRef.current = JSON.stringify(remote.snapshot)
          persistedRemoteWorkspaceRef.current = remote
          setWorkspace(remote.snapshot, false)
          clearHistory()
          setToast('Outro usuário atualizou estes dados. A versão mais recente foi carregada; sua edição não foi sobrescrita silenciosamente.')
        } else {
          setToast(error instanceof Error ? error.message : 'Não foi possível confirmar a alteração no servidor.')
        }
      } finally {
        workspaceSaveInFlightRef.current = false
        if (JSON.stringify(workspaceSnapshotRef.current) !== persistedWorkspaceRef.current) {
          workspaceSaveTimerRef.current = window.setTimeout(() => void persistLatestWorkspace(), 800)
        }
      }
    }

    workspaceSaveTimerRef.current = window.setTimeout(() => void persistLatestWorkspace(), 500)
    return () => window.clearTimeout(workspaceSaveTimerRef.current)
  }, [workspace, workspaceReady, clearHistory, setWorkspace])

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(''), 2300)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (!authenticated || page !== 'talentDesk' || canAccessTalentDesk(currentUserProfile)) return
    window.history.replaceState(null, '', pagePaths.dashboard)
    setPageState('dashboard')
    setToast('O Banco de Talentos é exclusivo para Administradores e Gerentes.')
  }, [authenticated, currentUserProfile, page])

  useEffect(() => {
    function syncPageFromLocation(clearHighlights = false) {
      const currentPath = normalizedPath(window.location.pathname)
      if (currentPath === '/tasks') {
        window.history.replaceState(null, '', pagePaths.tasks)
        setPageState('tasks')
        setSelectedTaskId(null)
      } else {
        const routeCampaignId = campaignIdFromPath(currentPath)
        if (routeCampaignId) {
          const routeCampaign = campaigns.find(campaign => campaign.id === routeCampaignId)
          const routeTask = tasks.find(task => task.campaignId === routeCampaignId)
          if (routeCampaign && routeTask && campaignIsVisible(activeAccessScope, routeCampaign)) {
            setPageState('tasks')
            setSelectedTaskId(routeTask.id)
          } else {
            window.history.replaceState(null, '', pagePaths.tasks)
            setPageState('tasks')
            setSelectedTaskId(null)
          }
        } else if (Object.values(pagePaths).includes(currentPath)) {
          setPageState(pageFromPath(currentPath))
          setSelectedTaskId(null)
        } else {
          window.history.replaceState(null, '', pagePaths.dashboard)
          setPageState('dashboard')
          setSelectedTaskId(null)
        }
      }
      if (clearHighlights) {
        setHighlightedTaskId(null)
        setPriorityHighlightedTaskIds([])
        document.querySelector('#sidebar')?.classList.remove('open')
      }
    }

    function handlePopState() {
      if (authenticated) syncPageFromLocation(true)
    }

    if (authenticated) syncPageFromLocation()
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [authenticated, campaigns, tasks, activeAccessScopeKey])

  function updateUserProfile(accountRole: UserKey, profile: UserProfile) {
    setUserProfiles(current => ({ ...current, [accountRole]: profile }))
    setToast(`Perfil de ${users[accountRole].name} atualizado para ${profile}.`)
  }

  function updateUserLevel(accountRole: UserKey, level: UserLevel) {
    setUserLevels(current => ({ ...current, [accountRole]: level }))
    setToast(`Nível de ${users[accountRole].name} atualizado para ${level}.`)
  }

  function updateUserAccessScope(accountRole: UserKey, scope: UserAccessScope) {
    if (role !== 'admin' || accountRole === 'admin') return
    setUserAccessScopes(current => ({ ...current, [accountRole]: scope }))
    setToast(`Acesso de ${users[accountRole].name} atualizado.`)
  }

  function updateRegisteredProfile(email: string, profile: UserProfile) {
    if (role !== 'admin') return
    setRegisteredAccounts(current => current.map(account => account.email === email ? { ...account, profile } : account))
    const mockRole = Object.entries(users).find(([, user]) => user.email.toLowerCase() === email)?.[0]
    if (mockRole) setUserProfiles(current => ({ ...current, [mockRole]: profile }))
    setToast(`Perfil de ${registeredAccounts.find(account => account.email === email)?.name ?? email} atualizado para ${profile}.`)
  }

  function updateRegisteredLevel(email: string, level: UserLevel) {
    if (role !== 'admin') return
    setRegisteredAccounts(current => current.map(account => account.email === email ? { ...account, level } : account))
    const mockRole = Object.entries(users).find(([, user]) => user.email.toLowerCase() === email)?.[0]
    if (mockRole) setUserLevels(current => ({ ...current, [mockRole]: level }))
    setToast(`Nível de ${registeredAccounts.find(account => account.email === email)?.name ?? email} atualizado para ${level}.`)
  }

  function updateRegisteredAccessScope(email: string, scope: UserAccessScope) {
    if (role !== 'admin' || registeredAccounts.find(account => account.email === email)?.systemRole === 'admin') return
    setRegisteredAccounts(current => current.map(account => account.email === email ? { ...account, accessScope: scope } : account))
    const mockRole = Object.entries(users).find(([, user]) => user.email.toLowerCase() === email)?.[0]
    if (mockRole) setUserAccessScopes(current => ({ ...current, [mockRole]: scope }))
    setToast(`Acesso de ${registeredAccounts.find(account => account.email === email)?.name ?? email} atualizado.`)
  }

  function setTestUser(nextUserKey: UserKey | null) {
    if (!import.meta.env.DEV || authenticatedUser?.systemRole !== 'admin') return
    const nextUser = nextUserKey ? users[nextUserKey] : undefined
    if (nextUserKey && !nextUser?.mock) return
    setSimulatedUserKey(nextUserKey)
    setRoleState(nextUser?.systemRole ?? authenticatedUser.systemRole)
    setSelectedTaskId(null)
    setHighlightedTaskId(null)
    setPriorityHighlightedTaskIds([])
    setModal(null)
    setEditingClientId(null)
    setToast(nextUserKey ? `Simulando ${users[nextUserKey].name} · ${userProfiles[nextUserKey]}.` : 'Simulação encerrada. Você voltou para sua conta real.')
  }

  function updateClient(client: Client, operationChanges: Record<string, string | undefined>) {
    if (role !== 'admin') return
    const previous = clients.find(item => item.id === client.id)
    if (!previous) return
    setClients(current => current.map(item => item.id === client.id ? client : item))
    setUserAccessScopes(current => Object.fromEntries(Object.entries(current).map(([accountRole, scope]) => {
      const clientAccess = scope.clients[client.id]
      if (!clientAccess || clientAccess.allOperations) return [accountRole, scope]
      const operations = clientAccess.operations.flatMap(operation => {
        const nextOperation = operation in operationChanges ? operationChanges[operation] : operation
        return nextOperation && client.operations.includes(nextOperation) ? [nextOperation] : []
      })
      return [accountRole, { ...scope, clients: { ...scope.clients, [client.id]: { ...clientAccess, operations: [...new Set(operations)] } } }]
    })) as UserAccessScopes)
    setRegisteredAccounts(current => current.map(account => {
      const clientAccess = account.accessScope.clients[client.id]
      if (!clientAccess || clientAccess.allOperations) return account
      const operations = clientAccess.operations.flatMap(operation => {
        const nextOperation = operation in operationChanges ? operationChanges[operation] : operation
        return nextOperation && client.operations.includes(nextOperation) ? [nextOperation] : []
      })
      return { ...account, accessScope: { ...account.accessScope, clients: { ...account.accessScope.clients, [client.id]: { ...clientAccess, operations: [...new Set(operations)] } } } }
    }))
    setCampaigns(current => current.map(campaign => {
      if (campaign.clientId !== client.id || !campaign.operation || !(campaign.operation in operationChanges)) return campaign
      const nextOperation = operationChanges[campaign.operation]
      if (nextOperation) return { ...campaign, operation: nextOperation }
      const { operation: _removedOperation, ...campaignWithoutOperation } = campaign
      return campaignWithoutOperation
    }))
    setPlatformActivities(current => [...current, {
      id: crypto.randomUUID(), user: currentUserInitials,
      description: `atualizou o cliente “${previous.name}” e suas operações`, context: 'Configurações',
      occurredAt: activityTimestamp(), occurredOn: new Date().toISOString(),
    }])
    setEditingClientId(null)
    setToast('Cliente e operações atualizados.')
  }

  function requestDeleteClient(client: Client) {
    if (role !== 'admin') return
    const linkedCampaigns = campaigns.filter(campaign => campaign.clientId === client.id)
    const linkedCampaignIds = new Set(linkedCampaigns.map(campaign => campaign.id))
    const linkedTasks = tasks.filter(task => linkedCampaignIds.has(task.campaignId))
    const linkedDescription = linkedCampaigns.length
      ? ` Também serão excluídas ${linkedCampaigns.length} ${linkedCampaigns.length === 1 ? 'campanha' : 'campanhas'} e ${linkedTasks.length} ${linkedTasks.length === 1 ? 'tarefa vinculada' : 'tarefas vinculadas'}.`
      : ''
    setConfirmation({
      title: `Excluir o cliente ${client.name}?`,
      description: `O cliente e suas ${client.operations.length} ${client.operations.length === 1 ? 'operação serão excluídos' : 'operações serão excluídos'}.${linkedDescription} Esta ação não poderá ser desfeita.`,
      confirmLabel: 'Excluir cliente',
      onConfirm: () => {
        setClients(current => current.filter(item => item.id !== client.id))
        setCampaigns(current => current.filter(campaign => campaign.clientId !== client.id))
        setTasks(current => current.filter(task => !linkedCampaignIds.has(task.campaignId)))
        setUserAccessScopes(current => Object.fromEntries(Object.entries(current).map(([accountRole, scope]) => {
          const nextClients = { ...scope.clients }
          delete nextClients[client.id]
          return [accountRole, { ...scope, clients: nextClients }]
        })) as UserAccessScopes)
        setRegisteredAccounts(current => current.map(account => {
          const nextClients = { ...account.accessScope.clients }
          delete nextClients[client.id]
          return { ...account, accessScope: { ...account.accessScope, clients: nextClients } }
        }))
        if (tasks.some(task => task.id === selectedTaskId && linkedCampaignIds.has(task.campaignId))) setSelectedTaskId(null)
        setEditingClientId(null)
        setPlatformActivities(current => [...current, {
          id: crypto.randomUUID(), user: currentUserInitials,
          description: `excluiu o cliente “${client.name}”`, context: 'Configurações',
          occurredAt: activityTimestamp(), occurredOn: new Date().toISOString(),
        }])
        setToast('Cliente, operações e vínculos excluídos.')
      },
    })
  }

  const handleAuthenticated = useCallback((user: AuthenticatedUser) => {
    const currentPath = normalizedPath(window.location.pathname)
    if (currentPath === '/tasks') window.history.replaceState(null, '', pagePaths.tasks)
    else if (!isKnownApplicationPath(currentPath)) window.history.replaceState(null, '', pagePaths.dashboard)
    setPageState(pageFromPath())
    finishAuthentication(user)
  }, [finishAuthentication])
  async function logout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } finally {
      window.google?.accounts.id.disableAutoSelect()
      setAuthenticatedUser(null)
      setAuthenticated(false)
      setWorkspaceReady(false)
      setWorkspaceError('')
      setRoleState('worker')
      setSimulatedUserKey(null)
      setSelectedTaskId(null)
      setHighlightedTaskId(null)
      setPriorityHighlightedTaskIds([])
      setModal(null)
      setEditingClientId(null)
      clearHistory()
      window.history.replaceState(null, '', '/')
      setPageState('dashboard')
    }
  }
  async function syncGoogleAvatar() {
    if (!authenticatedUser?.googleClientId) {
      setToast('Não foi possível iniciar a sincronização com o Google.')
      return
    }
    setAvatarSyncing(true)
    try {
      const profileAccessToken = await requestGoogleProfileToken(authenticatedUser.googleClientId, 'consent')
      if (!profileAccessToken) throw new Error('A autorização do perfil Google foi cancelada ou bloqueada pelo navegador.')
      const response = await fetch('/api/auth/profile', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${profileAccessToken}` },
        body: JSON.stringify({}),
      })
      const result = await response.json() as { user?: AuthenticatedUser; error?: string }
      if (!response.ok || !result.user) throw new Error(result.error || 'Não foi possível sincronizar a foto do Google.')
      setRegisteredAccounts(current => current.map(account => account.email === result.user!.email.toLowerCase() ? { ...account, picture: result.user!.picture ?? result.user!.avatarUrl } : account))
      finishAuthentication(result.user)
      setToast('Foto do Google sincronizada com sucesso.')
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Não foi possível sincronizar a foto do Google.')
    } finally {
      setAvatarSyncing(false)
    }
  }
  function setPage(nextPage: Page) {
    if (nextPage === 'talentDesk' && !canAccessTalentDesk(currentUserProfile)) {
      setToast('O Banco de Talentos é exclusivo para Administradores e Gerentes.')
      return
    }
    setPageState(nextPage)
    setSelectedTaskId(null)
    setHighlightedTaskId(null)
    setPriorityHighlightedTaskIds([])
    if (window.location.pathname !== pagePaths[nextPage]) window.history.pushState(null, '', pagePaths[nextPage])
    document.querySelector('#sidebar')?.classList.remove('open')
  }
  function createCampaign(campaign: Campaign, taskSeed: { owner: string; description?: string; startInBacklog: boolean }) {
    if (!canCreateCampaign) {
      setModal(null)
      setToast('Seu perfil não possui permissão para criar campanhas.')
      return
    }
    const creationScope = hasFullCampaignClientAccess(role, currentUserProfile) ? { allClients: true, clients: {} } : activeAccessScope
    if (!campaignIsVisible(creationScope, campaign)) {
      setModal(null)
      setToast('Este usuário não participa do cliente ou da operação selecionada.')
      return
    }
    const currentUser = currentUserInitials
    const initialStatus: TaskStatus = taskSeed.startInBacklog ? 'Backlog' : 'Em espera'
    const campaignTask: Task = {
      id: crypto.randomUUID(), title: campaign.name, description: taskSeed.description, campaignId: campaign.id,
      category: categoryByArea[campaign.reportArea ?? 'Gestão de Demandas'], reportArea: campaign.reportArea,
      priority: 'Média', status: initialStatus,
      due: campaign.endDate ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${campaign.endDate}T12:00:00`)) : 'Sem prazo',
      owner: taskSeed.owner, seconds: 0, running: false, timeEntries: [],
      activityEntries: [createActivityEntry(currentUser, initialStatus, `criou a tarefa “${campaign.name}” automaticamente com a campanha em ${initialStatus}`)],
      alterations: [],
      subtasks: [],
    }
    setCampaigns(current => [campaign, ...current])
    setTasks(current => [campaignTask, ...current])
    const assignmentNotification = createNewCampaignAssignmentNotification({
      creator: currentUser,
      recipient: taskSeed.owner,
      taskId: campaignTask.id,
      campaignId: campaign.id,
      assignedBy: currentUserName,
      campaignName: campaign.name,
    })
    if (assignmentNotification) setNotifications(current => [assignmentNotification, ...current])
    setModal(null)
    setPage('tasks')
    setHighlightedTaskId(campaignTask.id)
    setToast(assignmentNotification
      ? `Campanha criada em “${initialStatus}” e responsável notificado.`
      : `Campanha criada e adicionada em “${initialStatus}”.`)
  }
  function deleteCampaign(id: string) {
    if (role === 'worker') return
    const campaign = campaigns.find(item => item.id === id)
    if (!campaign) return
    if (!campaignIsVisible(activeAccessScope, campaign)) return
    const client = clients.find(item => item.id === campaign.clientId)
    const linkedTasks = tasks.filter(task => task.campaignId === id).length
    setConfirmation({
      title: 'Excluir campanha?',
      description: linkedTasks
        ? `A campanha “${campaign.name}” e ${linkedTasks} ${linkedTasks === 1 ? 'tarefa vinculada serão excluídas' : 'tarefas vinculadas serão excluídas'}. Esta ação não poderá ser desfeita.`
        : `A campanha “${campaign.name}” será excluída permanentemente. Esta ação não poderá ser desfeita.`,
      confirmLabel: 'Excluir campanha',
      onConfirm: () => {
        setCampaigns(current => current.filter(item => item.id !== id))
        setTasks(current => current.filter(task => task.campaignId !== id))
        if (tasks.some(task => task.id === selectedTaskId && task.campaignId === id)) setSelectedTaskId(null)
        setPlatformActivities(current => [...current, {
          id: crypto.randomUUID(), user: currentUserInitials,
          description: `excluiu a campanha “${campaign.name}”`,
          context: `${client?.name ?? 'Cliente'}${campaign.operation ? ` · ${campaign.operation}` : ''}`,
          occurredAt: activityTimestamp(), occurredOn: new Date().toISOString(),
        }])
        setToast('Campanha excluída e registrada no histórico.')
      },
    })
  }
  function requestDeleteCampaignTask(id: string) {
    const task = tasks.find(item => item.id === id)
    const campaign = campaigns.find(item => item.id === task?.campaignId)
    if (!task || !campaign) return
    if (!canDeleteCampaignTask(currentUserProfile, currentUserInitials, campaign)) {
      setToast('Somente administradores e o criador da campanha podem excluir esta tarefa.')
      return
    }
    setConfirmation({
      title: 'Excluir tarefa?',
      description: 'Tem certeza que deseja excluir? Essa tarefa não pode ser desfeita.',
      confirmLabel: 'Sim',
      cancelLabel: 'Não',
      onConfirm: () => {
        setTasks(current => current.filter(item => item.id !== id))
        setNotifications(current => current.filter(notification => notification.taskId !== id))
        if (selectedTaskId === id) setSelectedTaskId(null)
        if (highlightedTaskId === id) setHighlightedTaskId(null)
        setPlatformActivities(current => [...current, {
          id: crypto.randomUUID(), user: currentUserInitials,
          description: `excluiu a tarefa “${task.title}”`,
          context: campaign.name,
          occurredAt: activityTimestamp(), occurredOn: new Date().toISOString(),
        }])
        setToast('Tarefa excluída.')
      },
    })
  }
  function toggleDone(id: string) {
    const taskToChange = tasks.find(task => task.id === id)
    if (!taskToChange) return
    const currentUser = currentUserInitials
    if (!canOverrideStatus && role === 'worker' && taskToChange.owner !== currentUser) {
      setToast(`Somente o responsável pela tarefa pode ${taskToChange.status === 'Aprovada' ? 'reabrir sua conclusão' : 'marcá-la como feita'}.`)
      return
    }
    setTasks(current => current.map(task => {
      if (task.id !== id) return task
      const status: TaskStatus = task.status === 'Aprovada' ? 'Em espera' : 'Aprovada'
      return {
        ...task,
        running: false,
        runningUser: undefined,
        activeTimeEntryId: undefined,
        status,
        activityEntries: [...task.activityEntries, statusChangeActivity(currentUser, 'task', task.id, task.title, task.status, status)],
      }
    }))
    setToast(taskToChange.status === 'Aprovada' ? `“${taskToChange.title}” foi reaberta.` : `“${taskToChange.title}” foi marcada como concluída.`)
  }
  function updateTask(id: string, changes: Partial<Task>) {
    const taskToUpdate = tasks.find(task => task.id === id)
    const taskCampaign = campaigns.find(campaign => campaign.id === taskToUpdate?.campaignId)
    const currentUser = currentUserInitials
    const isCreatorOwnerChange = taskCampaign?.createdBy === currentUserInitials &&
      Object.keys(changes).length === 1 && Object.prototype.hasOwnProperty.call(changes, 'owner')
    const isAssignedOwnerDetailChange = taskToUpdate && canEditAssignedCampaignDetails(role, taskToUpdate.owner, currentUser, changes)
    if (role === 'worker' && !isCreatorOwnerChange && !isAssignedOwnerDetailChange) {
      setToast('O responsável pode editar apenas o status, a área e a prioridade da campanha.')
      return
    }
    const nextChanges = { ...changes }
    if (typeof nextChanges.title === 'string') {
      nextChanges.title = nextChanges.title.trim()
      if (!nextChanges.title) return
    }
    if (taskToUpdate && nextChanges.status === 'Em análise' && taskToUpdate.status !== 'Em análise' && !canMoveCampaignToReview(taskToUpdate, currentUserProfile)) {
      setToast('Conclua todas as tarefas vinculadas antes de mover a campanha para “Em análise”.')
      return
    }
    setTasks(current => current.map(task => {
      if (task.id !== id) return task
      let description = 'editou os dados da demanda'
      if (nextChanges.priority && nextChanges.priority !== task.priority) description = `alterou a prioridade de ${task.priority} para ${nextChanges.priority}`
      if (nextChanges.reportArea && nextChanges.reportArea !== task.reportArea) description = `alterou a área de ${task.reportArea ?? 'não definida'} para ${nextChanges.reportArea}`
      if (nextChanges.title && nextChanges.title !== task.title) description = `renomeou a demanda “${task.title}” para “${nextChanges.title}”`
      if (nextChanges.owner && nextChanges.owner !== task.owner) description = `alterou o responsável da campanha de ${collaboratorName(task.owner)} para ${collaboratorName(nextChanges.owner)}`
      const activityEntry = nextChanges.status && nextChanges.status !== task.status
        ? statusChangeActivity(currentUser, 'task', task.id, task.title, task.status, nextChanges.status)
        : createActivityEntry(currentUser, nextChanges.status ?? task.status, description)
      return {
        ...task,
        ...nextChanges,
        activityEntries: [...task.activityEntries, activityEntry],
      }
    }))
    if (typeof nextChanges.owner === 'string' && taskCampaign) {
      setCampaigns(current => current.map(campaign => campaign.id === taskCampaign.id
        ? { ...campaign, members: [...new Set([...campaign.members, nextChanges.owner!])] }
        : campaign))
    }
    if (nextChanges.reportArea && taskCampaign) {
      setCampaigns(current => current.map(campaign => campaign.id === taskCampaign.id
        ? { ...campaign, reportArea: nextChanges.reportArea }
        : campaign))
    }
    if (taskToUpdate && taskCampaign && typeof nextChanges.owner === 'string' &&
      nextChanges.owner !== taskToUpdate.owner && nextChanges.owner !== currentUser) {
      const assignmentNotification = createAssignmentNotification({
        recipient: nextChanges.owner,
        taskId: taskToUpdate.id,
        campaignId: taskToUpdate.campaignId,
        assignedBy: currentUserName,
        campaignName: taskCampaign.name,
      })
      setNotifications(current => [assignmentNotification, ...current])
    }
  }
  function changeTaskStatus(id: string, status: TaskStatus, placement?: TaskDropPlacement) {
    const taskToChange = tasks.find(task => task.id === id)
    if (!taskToChange) return
    if (taskToChange.status === 'Em análise' && status === 'Aprovada') {
      approveCampaignSubtasks(id, taskToChange.subtasks.map(subtask => subtask.id))
      return
    }
    if (status === 'Em análise' && taskToChange.status !== 'Em análise' && !canMoveCampaignToReview(taskToChange, currentUserProfile)) {
      setToast('Conclua todas as tarefas vinculadas antes de mover a campanha para “Em análise”.')
      return
    }
    if (!canOverrideStatus && taskToChange.status === 'Em análise' && status === 'Em alteração') {
      setToast('Sinalize as tarefas e justificativas no componente “Alterações”.')
      return
    }
    if (!canOverrideStatus && status === 'Em execução' && taskToChange.status !== 'Em execução') {
      setToast('A campanha entra em “Em execução” quando alguém inicia uma tarefa ou subtarefa.')
      return
    }
    const currentUser = currentUserInitials
    const isReviewerApproval = taskToChange.status === 'Em análise' && status === 'Aprovada' && canReviewCampaign(taskToChange, currentUser)
    if (!canOverrideStatus && !isReviewerApproval && !canEditCampaignPanel(role, taskToChange.owner, currentUser)) {
      setToast('Somente o responsável pela campanha pode editar seu status pelo painel.')
      return
    }
    setTasks(current => {
      const updatedTasks = current.map(task => {
        if (task.id !== id || task.status === status) return task
        const activityEntry = statusChangeActivity(currentUser, 'task', task.id, task.title, task.status, status)
        if (status === 'Aprovada' || !task.running) {
          return { ...task, status, running: status === 'Aprovada' ? false : task.running, runningUser: status === 'Aprovada' ? undefined : task.runningUser, activeTimeEntryId: status === 'Aprovada' ? undefined : task.activeTimeEntryId, activityEntries: [...task.activityEntries, activityEntry] }
        }

        const entryId = crypto.randomUUID()
        const startedAt = activityTimestamp()
        return {
          ...task,
          status,
          activeTimeEntryId: entryId,
          timeEntries: [...task.timeEntries, { id: entryId, user: task.runningUser ?? currentUserInitials, status, seconds: 0, startedAt }],
          activityEntries: [...task.activityEntries, activityEntry],
        }
      })
      return placement ? reorderCampaignTask(updatedTasks, id, status, placement) : updatedTasks
    })
  }
  function requestCampaignAlterations(taskId: string, requestedItems: RequestedCampaignAlteration[]) {
    const task = tasks.find(item => item.id === taskId)
    if (!task) return
    if (task.status !== 'Em análise') {
      setToast('A campanha precisa estar em análise para solicitar alterações.')
      return
    }
    const currentUser = currentUserInitials
    if (!canOverrideStatus && !canReviewCampaign(task, currentUser)) {
      setToast('Somente o responsável ou um revisor personalizado pode reprovar a análise.')
      return
    }
    const alteration = createCampaignAlteration(task, requestedItems, currentUser)
    if (!alteration) {
      setToast('Selecione ao menos uma tarefa e informe a justificativa.')
      return
    }
    const selectedIds = new Set(alteration.items.map(item => item.subtaskId))
    const alterationActivity = statusChangeActivity(
      currentUser, 'task', task.id, task.title, task.status, 'Em alteração',
      `reprovou a análise da campanha e solicitou alteração em ${alteration.items.length} ${alteration.items.length === 1 ? 'tarefa' : 'tarefas'}`,
    )
    setTasks(current => current.map(item => {
      if (item.id !== taskId) return item
      const selectedSubtasks = item.subtasks.filter(subtask => selectedIds.has(subtask.id))
      const reopenedTask = applyCampaignAlteration(item, alteration)
      return {
        ...reopenedTask,
        activityEntries: [
          ...item.activityEntries,
          alterationActivity,
          ...selectedSubtasks.map(subtask => statusChangeActivity(
            currentUser, 'deliverable', subtask.id, subtask.title, subtask.status, 'Em alteração',
            `reprovou a entrega “${subtask.title}” na análise da campanha`,
          )),
        ],
      }
    }))

    const campaign = campaigns.find(item => item.id === task.campaignId)
    const recipients = [...new Set([
      ...(campaign?.members ?? []),
      ...alteration.items.map(item => item.owner),
    ])].filter(recipient => recipient !== currentUser)
    if (recipients.length) {
      const createdOn = new Date(alteration.createdOn)
      setNotifications(current => [
        ...recipients.map(recipient => createCampaignAlterationNotification({
          recipient,
          taskId: task.id,
          campaignId: task.campaignId,
          requestedBy: currentUserName,
          campaignName: campaign?.name ?? task.title,
          alterationCount: alteration.items.length,
          createdOn,
        })),
        ...current,
      ])
    }
    setToast(`${alteration.items.length} ${alteration.items.length === 1 ? 'alteração solicitada' : 'alterações solicitadas'} e envolvidos notificados.`)
  }
  function approveCampaignSubtasks(taskId: string, subtaskIds: string[]) {
    const task = tasks.find(item => item.id === taskId)
    if (!task || task.status !== 'Em análise') {
      setToast('A campanha precisa estar em análise para aprovar tarefas.')
      return
    }
    const currentUser = currentUserInitials
    if (!canOverrideStatus && !canReviewCampaign(task, currentUser)) {
      setToast('Somente o responsável ou um revisor personalizado pode aprovar tarefas na análise.')
      return
    }
    const approval = approveCampaignReviewSubtasks(task, subtaskIds, currentUser)
    if (!approval) {
      setToast('Selecione ao menos uma tarefa concluída que ainda aguarda análise.')
      return
    }
    const approvedIds = new Set(approval.approvedSubtaskIds)
    const campaignApproved = approval.status === 'Aprovada'
    setTasks(current => current.map(item => {
      if (item.id !== taskId) return item
      const approvalActivities = item.subtasks
        .filter(subtask => approvedIds.has(subtask.id))
        .map(subtask => createActivityEntry(currentUser, 'Aprovada', `aprovou a tarefa “${subtask.title}” na análise da campanha`))
      const campaignActivity = campaignApproved
        ? statusChangeActivity(
            currentUser, 'task', item.id, item.title, item.status, 'Aprovada',
            'aprovou a campanha após a validação individual de todas as tarefas',
          )
        : undefined
      return {
        ...item,
        status: approval.status,
        subtasks: approval.subtasks,
        running: campaignApproved ? false : item.running,
        runningUser: campaignApproved ? undefined : item.runningUser,
        activeTimeEntryId: campaignApproved ? undefined : item.activeTimeEntryId,
        activityEntries: [...item.activityEntries, ...approvalActivities, ...(campaignActivity ? [campaignActivity] : [])],
      }
    }))
    setToast(campaignApproved
      ? 'Todas as tarefas foram aprovadas. A campanha foi concluída.'
      : `${approval.approvedSubtaskIds.length} ${approval.approvedSubtaskIds.length === 1 ? 'tarefa aprovada' : 'tarefas aprovadas'} na análise.`)
  }
  function withdrawCampaignSubtaskApproval(taskId: string, subtaskId: string) {
    const task = tasks.find(item => item.id === taskId)
    if (!task || !canWithdrawCampaignTaskApproval(currentUserProfile)) {
      setToast('Somente Administradores, Gerentes e Coordenadores podem retirar aprovações.')
      return
    }
    const subtask = task.subtasks.find(item => item.id === subtaskId)
    if (!withdrawCampaignReviewSubtaskApproval(task, subtaskId) || !subtask) {
      setToast('Essa tarefa não possui uma aprovação ativa para retirar.')
      return
    }

    setTasks(current => current.map(item => {
      if (item.id !== taskId) return item
      const currentWithdrawal = withdrawCampaignReviewSubtaskApproval(item, subtaskId)
      const currentSubtask = item.subtasks.find(candidate => candidate.id === subtaskId)
      if (!currentWithdrawal || !currentSubtask) return item
      const approvalActivity = createActivityEntry(currentUserInitials, 'Em análise', `retirou a aprovação da tarefa “${currentSubtask.title}” na análise da campanha`)
      const campaignActivity = item.status === 'Aprovada'
        ? statusChangeActivity(
            currentUserInitials, 'task', item.id, item.title, item.status, 'Em análise',
            `retornou a campanha para análise ao retirar a aprovação da tarefa “${currentSubtask.title}”`,
          )
        : undefined
      return {
        ...item,
        status: currentWithdrawal.status,
        subtasks: currentWithdrawal.subtasks,
        activityEntries: [...item.activityEntries, approvalActivity, ...(campaignActivity ? [campaignActivity] : [])],
      }
    }))
    setToast(`A aprovação de “${subtask.title}” foi retirada.`)
  }
  function requestWithdrawCampaignSubtaskApproval(taskId: string, subtaskId: string) {
    const task = tasks.find(item => item.id === taskId)
    const subtask = task?.subtasks.find(item => item.id === subtaskId)
    if (!task || !subtask || !canWithdrawCampaignTaskApproval(currentUserProfile) || !withdrawCampaignReviewSubtaskApproval(task, subtaskId)) {
      setToast('Essa aprovação não pode ser retirada.')
      return
    }
    setConfirmation({
      title: 'Retirar aprovação',
      description: 'Confirme para remover a aprovação dessa tarefa.',
      confirmLabel: 'Confirmar',
      cancelLabel: 'Cancelar',
      tone: 'warning',
      onConfirm: () => withdrawCampaignSubtaskApproval(taskId, subtaskId),
    })
  }
  function addSubtask(taskId: string, materialCategory: MaterialCategory, material: string, volume: number, pages: number) {
    const targetTask = tasks.find(task => task.id === taskId)
    const targetCampaign = campaigns.find(campaign => campaign.id === targetTask?.campaignId)
    if (!targetCampaign || !canCreateTaskInCampaign({ profile: currentUserProfile, accessScope: activeAccessScope }, targetCampaign)) {
      setToast('Este usuário não participa do cliente ou da operação desta campanha.')
      return
    }
    const cleanTitle = material.trim()
    if (!cleanTitle) return
    const currentUser = currentUserInitials
    const normalizedVolume = normalizeSubtaskVolume(volume)
    const { format, kind } = defaultSubtaskPresentation(materialCategory)
    const normalizedPages = format === 'Jornal' ? normalizeJournalPages(pages) : 1
    setTasks(current => current.map(task => {
      if (task.id !== taskId) return task
      const createdSubtask: Subtask = {
        id: crypto.randomUUID(), title: cleanTitle, materialCategory, reportArea: task.reportArea,
        dueAt: toLocalDateTimeValue(parseTaskDeadline(task.due)) || undefined,
        createdBy: currentUser, owner: currentUser, status: 'Em espera', seconds: 0, running: false,
        supportUsers: [], timeEntries: [], format, kind, volume: normalizedVolume, pages: normalizedPages,
      }
      return {
        ...task,
        subtasks: [...task.subtasks, createdSubtask],
        activityEntries: [...task.activityEntries, createActivityEntry(currentUser, createdSubtask.status, `criou a tarefa “${cleanTitle}” · Tipo: ${kind} · Volume: ${normalizedVolume}${format === 'Jornal' ? ` · Páginas: ${normalizedPages}` : ''}`)],
      }
    }))
    setToast('Subtarefa adicionada.')
  }
  function addChildSubtask(taskId: string, parentId: string, title: string, kind: SubtaskKind, volume: number, pages: number) {
    const targetTask = tasks.find(task => task.id === taskId)
    const targetCampaign = campaigns.find(campaign => campaign.id === targetTask?.campaignId)
    if (!targetCampaign || !canCreateTaskInCampaign({ profile: currentUserProfile, accessScope: activeAccessScope }, targetCampaign)) {
      setToast('Este usuário não participa do cliente ou da operação desta campanha.')
      return
    }
    const cleanTitle = title.trim()
    if (!cleanTitle) return
    const currentTask = tasks.find(task => task.id === taskId)
    const currentParent = currentTask?.subtasks.find(item => item.id === parentId)
    if (!currentParent) return
    const currentUser = currentUserInitials
    const normalizedVolume = normalizeSubtaskVolume(volume)
    const normalizedPages = currentParent.format === 'Jornal' ? normalizeJournalPages(pages) : 1
    setTasks(current => current.map(task => {
      if (task.id !== taskId) return task
      const parent = task.subtasks.find(item => item.id === parentId)
      if (!parent) return task
      const child: Subtask = {
        id: crypto.randomUUID(), parentId, title: cleanTitle, materialCategory: parent.materialCategory,
        dueAt: parent.dueAt ?? (toLocalDateTimeValue(parseTaskDeadline(task.due)) || undefined),
        reportArea: parent.reportArea ?? task.reportArea, createdBy: currentUser, owner: role === 'worker' ? currentUser : parent.owner, status: 'Em espera', seconds: 0,
        supportUsers: [], timeEntries: [], running: false, format: parent.format, kind, volume: normalizedVolume, pages: normalizedPages,
      }
      const withChild = [...task.subtasks, child]
      const invalidatedTasks = withChild.filter(item => item.status === 'Aprovada' && !dependencyState(withChild, item).satisfied)
      const nextSubtasks = withChild.map(item => invalidatedTasks.some(invalidated => invalidated.id === item.id)
        ? { ...item, status: 'Em espera' as TaskStatus, completedAt: undefined, analysisApprovedAt: undefined, analysisApprovedBy: undefined, running: false }
        : item)
      const creationEntry = createActivityEntry(currentUser, child.status, `criou a subtarefa “${cleanTitle}” · Tipo: ${kind} · Volume: ${normalizedVolume}${parent.format === 'Jornal' ? ` · Páginas: ${normalizedPages}` : ''} · Vinculada a “${parent.title}”`)
      const reopenedEntries = invalidatedTasks.map(item => statusChangeActivity(
        currentUser, 'deliverable', item.id, item.title, 'Aprovada', 'Em espera',
        `reabriu automaticamente a tarefa “${item.title}” após a criação da subtarefa “${cleanTitle}”`,
      ))
      return {
        ...task,
        subtasks: nextSubtasks,
        activityEntries: [...task.activityEntries, creationEntry, ...reopenedEntries],
      }
    }))
    setToast('Subtarefa vinculada com sucesso.')
  }
  function duplicateSubtask(taskId: string, subtaskId: string) {
    const targetTask = tasks.find(task => task.id === taskId)
    const targetCampaign = campaigns.find(campaign => campaign.id === targetTask?.campaignId)
    const source = targetTask?.subtasks.find(subtask => subtask.id === subtaskId)
    if (!targetTask || !targetCampaign || !source) return
    if (!canCreateTaskInCampaign({ profile: currentUserProfile, accessScope: activeAccessScope }, targetCampaign)) {
      setToast('Este usuário não participa do cliente ou da operação desta campanha.')
      return
    }
    const currentUser = currentUserInitials
    const duplicate = createSubtaskDuplicate(source, crypto.randomUUID(), currentUser)
    setTasks(current => current.map(task => {
      if (task.id !== taskId) return task
      const sourceIndex = task.subtasks.findIndex(item => item.id === subtaskId)
      if (sourceIndex < 0) return task
      const subtasks = [...task.subtasks]
      subtasks.splice(sourceIndex + 1, 0, duplicate)
      return {
        ...task,
        subtasks,
        activityEntries: [...task.activityEntries, createActivityEntry(currentUser, duplicate.status, `desdobrou a tarefa “${source.title}” como “${duplicate.title}”`)],
      }
    }))
    setToast('Tarefa desdobrada com sucesso.')
  }
  function updateSubtask(taskId: string, subtaskId: string, changes: Partial<Subtask>, analysisReopenConfirmed = false) {
    const currentUser = currentUserInitials
    const currentTask = tasks.find(task => task.id === taskId)
    const currentSubtask = currentTask?.subtasks.find(item => item.id === subtaskId)
    const changedFields = Object.keys(changes)
    const isCompletionToggle = currentSubtask && changedFields.length === 1 && changedFields[0] === 'status' && (
      (changes.status === 'Aprovada' && currentSubtask.status !== 'Aprovada') ||
      (changes.status === 'Em espera' && currentSubtask.status === 'Aprovada')
    )
    const isAdministratorStatusOverride = canOverrideStatus && changedFields.length === 1 && changedFields[0] === 'status'
    const isAssignedWorkerCompletionToggle = role === 'worker' && currentSubtask?.owner === currentUser && isCompletionToggle
    const isAssignedReviewerStatusUpdate = role === 'worker' && currentSubtask?.reviewer === currentUser && changedFields.length === 1 && changedFields[0] === 'status'
    const isCreatorJournalPagesEdit = role === 'worker' && currentSubtask?.format === 'Jornal' &&
      canEditJournalPages(false, currentSubtask.createdBy, currentUser) && changedFields.length === 1 && changedFields[0] === 'pages'
    const isSupportUsersEdit = changedFields.length === 1 && changedFields[0] === 'supportUsers'
    const isWorkerSupportEdit = role === 'worker' && isSupportUsersEdit &&
      (currentSubtask?.createdBy === currentUser || currentSubtask?.owner === currentUser)
    if (role === 'worker' && !isAssignedWorkerCompletionToggle && !isAssignedReviewerStatusUpdate && !isAdministratorStatusOverride && !isCreatorJournalPagesEdit && !isWorkerSupportEdit) {
      if (currentSubtask?.owner !== currentUser && currentSubtask?.reviewer !== currentUser) setToast(`Somente o responsável ou o revisor personalizado pode ${currentSubtask?.status === 'Aprovada' ? 'reabrir sua conclusão' : 'marcá-la como feita'}.`)
      else setToast('Colaboradores podem apenas iniciar, pausar, concluir ou reabrir as tarefas atribuídas a eles.')
      return
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'owner') && role === 'worker') {
      setToast('Somente gestores e administradores podem atribuir o responsável pela tarefa.')
      return
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'reviewer') && role === 'worker') {
      setToast('Somente gestores e administradores podem definir o revisor personalizado.')
      return
    }
    if (typeof changes.reviewer === 'string' && changes.reviewer === (changes.owner ?? currentSubtask?.owner)) {
      setToast('O revisor personalizado precisa ser diferente do responsável pela tarefa.')
      return
    }
    if (Object.prototype.hasOwnProperty.call(changes, 'title') && role === 'worker') {
      setToast('Somente gestores e administradores podem editar o nome das tarefas.')
      return
    }
    if (typeof changes.title === 'string' && !changes.title.trim()) return
    const targetKind = changes.kind ?? currentSubtask?.kind
    if (changes.kind && currentTask && currentSubtask && changes.kind !== currentSubtask.kind) {
      const alreadyInvalid = new Set(currentTask.subtasks
        .filter(item => item.status === 'Aprovada' && !dependencyState(currentTask.subtasks, item).satisfied)
        .map(item => item.id))
      const simulatedSubtasks = currentTask.subtasks.map(item => item.id === subtaskId ? { ...item, kind: changes.kind! } : item)
      const newlyInvalid = simulatedSubtasks.filter(item =>
        item.status === 'Aprovada' && !alreadyInvalid.has(item.id) && !dependencyState(simulatedSubtasks, item).satisfied)
      if (newlyInvalid.length) {
        setToast(`A alteração bloquearia tarefas concluídas: ${newlyInvalid.map(item => item.title).join(', ')}.`)
        return
      }
    }
    const dependencies = currentTask && currentSubtask && targetKind ? dependencyState(currentTask.subtasks, currentSubtask, targetKind) : undefined
    if (!isAdministratorStatusOverride && (changes.status === 'Aprovada' || Boolean(changes.kind && currentSubtask?.status === 'Aprovada')) && dependencies && !dependencies.satisfied) {
      setToast(`Conclua primeiro: ${dependencies.blockers.map(item => item.title).join(', ')}.`)
      return
    }
    const reopenReviewTransition = campaignReviewReopenTransition(currentTask, subtaskId, changes.status, currentUserProfile)
    if (reopenReviewTransition && !analysisReopenConfirmed) {
      setConfirmation({
        title: 'Desmarcar tarefa?',
        description: 'Desmarcar essa tarefa mudará o status da campanha de “Em análise” para “Em execução”.',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        tone: 'warning',
        onConfirm: () => updateSubtask(taskId, subtaskId, changes, true),
      })
      return
    }
    const reviewTransition = campaignReviewTransition(currentTask, subtaskId, changes.status)
    const completesAllTasks = Boolean(reviewTransition)
    const reviewActivity = completesAllTasks && currentTask
      ? statusChangeActivity(
          currentUser, 'task', currentTask.id, currentTask.title, currentTask.status, 'Em análise',
          'moveu a campanha automaticamente para Em análise após a conclusão de todas as tarefas',
        )
      : undefined
    const reopenReviewActivity = reopenReviewTransition && currentTask
      ? statusChangeActivity(
          currentUser, 'task', currentTask.id, currentTask.title, currentTask.status, reopenReviewTransition.status,
          `moveu a campanha para Em execução após desmarcar a tarefa “${currentSubtask?.title ?? ''}” como feita`,
        )
      : undefined
    const reviewNotifications: SystemNotification[] = completesAllTasks && currentTask
      ? (() => {
          const campaign = campaigns.find(item => item.id === currentTask.campaignId)
          const createdOn = new Date()
          return reviewTransition!.recipients.map(recipient => ({
            id: crypto.randomUUID(), recipient, taskId: currentTask.id, campaignId: currentTask.campaignId,
            title: 'Campanha pronta para análise',
            message: `Todas as tarefas de “${campaign?.name ?? currentTask.title}” foram concluídas. Revise a campanha.`,
            createdAt: activityTimestamp(createdOn), createdOn: createdOn.toISOString(),
          }))
        })()
      : []
    const assignmentNotification: SystemNotification | undefined = currentTask && currentSubtask &&
      typeof changes.owner === 'string' && changes.owner !== currentSubtask.owner && changes.owner !== currentUser
      ? createAssignmentNotification({
          recipient: changes.owner,
          taskId: currentTask.id,
          campaignId: currentTask.campaignId,
          assignedBy: currentUserName,
          campaignName: campaigns.find(item => item.id === currentTask.campaignId)?.name ?? currentTask.title,
          taskTitle: currentSubtask.title,
        })
      : undefined
    const reviewerNotification: SystemNotification | undefined = currentTask && currentSubtask &&
      typeof changes.reviewer === 'string' && changes.reviewer !== currentSubtask.reviewer && changes.reviewer !== currentUser
      ? createAssignmentNotification({
          recipient: changes.reviewer,
          taskId: currentTask.id,
          campaignId: currentTask.campaignId,
          assignedBy: currentUserName,
          campaignName: campaigns.find(item => item.id === currentTask.campaignId)?.name ?? currentTask.title,
          taskTitle: currentSubtask.title,
          assignmentKind: 'reviewer',
        })
      : undefined
    const alterationResolvedAt = changes.status === 'Aprovada' ? new Date().toISOString() : undefined
    setTasks(current => current.map(task => {
      if (task.id !== taskId) return task
      const subtask = task.subtasks.find(item => item.id === subtaskId)
      if (!subtask) return task
      const nextChanges = { ...changes }
      if (typeof nextChanges.title === 'string') nextChanges.title = nextChanges.title.trim()
      if (typeof nextChanges.volume === 'number') nextChanges.volume = normalizeSubtaskVolume(nextChanges.volume)
      if (nextChanges.supportUsers) nextChanges.supportUsers = normalizeTaskSupportUsers(nextChanges.supportUsers, nextChanges.owner ?? subtask.owner)
      if (nextChanges.owner && nextChanges.owner !== subtask.owner) nextChanges.supportUsers = normalizeTaskSupportUsers(nextChanges.supportUsers ?? subtask.supportUsers, nextChanges.owner)
      if (nextChanges.owner && nextChanges.owner === subtask.reviewer) nextChanges.reviewer = undefined
      if ((nextChanges.format ?? subtask.format) === 'Jornal' && (typeof nextChanges.pages === 'number' || nextChanges.format === 'Jornal')) nextChanges.pages = normalizeJournalPages(nextChanges.pages ?? subtask.pages)
      if (changes.status) {
        nextChanges.completedAt = changes.status === 'Aprovada' ? subtask.completedAt ?? new Date().toISOString().slice(0, 10) : undefined
        if (changes.status !== 'Aprovada') {
          nextChanges.analysisApprovedAt = undefined
          nextChanges.analysisApprovedBy = undefined
        }
      }
      let description = `editou a entrega “${subtask.title}”`
      const statusChanged = Boolean(changes.status && changes.status !== subtask.status)
      if (changes.owner && changes.owner !== subtask.owner) description = `atribuiu a subtarefa “${subtask.title}” para ${collaboratorName(changes.owner)}`
      if (Object.prototype.hasOwnProperty.call(changes, 'reviewer')) description = changes.reviewer
        ? `definiu ${collaboratorName(changes.reviewer)} como revisor personalizado da tarefa “${subtask.title}”`
        : `removeu o revisor personalizado da tarefa “${subtask.title}”`
      if (changes.supportUsers) description = `atualizou os usuários de suporte da tarefa “${subtask.title}”`
      if (nextChanges.title && nextChanges.title !== subtask.title) description = `renomeou a tarefa “${subtask.title}” para “${nextChanges.title}”`
      const activityEntry = statusChanged
        ? statusChangeActivity(currentUser, 'deliverable', subtask.id, subtask.title, subtask.status, changes.status as TaskStatus)
        : createActivityEntry(currentUser, subtask.status, description)
      const reopenedDependents = !isAdministratorStatusOverride && changes.status !== 'Aprovada' && subtask.status === 'Aprovada'
        ? completedDependents(task.subtasks, subtask)
        : []
      const reopenedIds = new Set(reopenedDependents.map(item => item.id))
      return {
        ...task,
        status: reopenReviewTransition?.status ?? reviewTransition?.status ?? task.status,
        running: completesAllTasks ? false : task.running,
        runningUser: completesAllTasks ? undefined : task.runningUser,
        activeTimeEntryId: completesAllTasks ? undefined : task.activeTimeEntryId,
        alterations: alterationResolvedAt
          ? resolveSubtaskAlterations(task.alterations, subtaskId, alterationResolvedAt)
          : task.alterations,
        subtasks: task.subtasks.map(item => {
          if (item.id === subtaskId) {
            const updated = { ...item, ...nextChanges }
            if (changes.status === 'Aprovada') return { ...updated, ...stopSubtaskTimers(updated) }
            if (changes.owner !== undefined || changes.supportUsers !== undefined || changes.format !== undefined) {
              return { ...updated, ...stopSubtaskTimers(updated, new Set([updated.owner, ...(updated.supportUsers ?? [])])) }
            }
            return updated
          }
          if (reopenedIds.has(item.id)) return { ...item, status: 'Em espera' as TaskStatus, completedAt: undefined, analysisApprovedAt: undefined, analysisApprovedBy: undefined, running: false }
          return item
        }),
        activityEntries: [
          ...task.activityEntries,
          activityEntry,
          ...reopenedDependents.map(item => statusChangeActivity(
            currentUser, 'deliverable', item.id, item.title, 'Aprovada', 'Em espera',
            `reabriu automaticamente a tarefa “${item.title}” após a reabertura de “${subtask.title}”`,
          )),
          ...(reviewActivity ? [reviewActivity] : []),
          ...(reopenReviewActivity ? [reopenReviewActivity] : []),
        ],
      }
    }))
    if (currentTask && currentSubtask && (Object.prototype.hasOwnProperty.call(changes, 'supportUsers') || Boolean(changes.reviewer))) {
      const supportUsers = changes.supportUsers ? normalizeTaskSupportUsers(changes.supportUsers, currentSubtask.owner) : []
      setCampaigns(current => current.map(campaign => campaign.id === currentTask.campaignId
        ? { ...campaign, members: [...new Set([...campaign.members, ...supportUsers, ...(changes.reviewer ? [changes.reviewer] : [])])] }
        : campaign))
    }
    const generatedNotifications = [assignmentNotification, reviewerNotification, ...reviewNotifications]
      .filter((notification): notification is SystemNotification => Boolean(notification))
    if (generatedNotifications.length) setNotifications(current => [...generatedNotifications, ...current])
    if (reviewNotifications.length) {
      setToast('Todas as tarefas foram concluídas. A campanha está em análise.')
    } else if (reopenReviewTransition) {
      setToast('Tarefa desmarcada. A campanha voltou para “Em execução”.')
    } else if (reviewerNotification) {
      setToast('Revisor personalizado atualizado e notificado.')
    } else if (assignmentNotification) {
      setToast('Responsável atualizado e notificado.')
    } else if (Object.prototype.hasOwnProperty.call(changes, 'reviewer')) {
      setToast(changes.reviewer ? 'Revisor personalizado atualizado.' : 'Revisor personalizado removido.')
    } else if (changes.supportUsers) {
      setToast('Equipe de suporte da tarefa atualizada.')
    }
  }
  function requestDeleteSubtask(taskId: string, subtaskId: string) {
    if (role === 'worker') {
      setToast('Colaboradores não podem excluir tarefas de campanhas.')
      return
    }
    const task = tasks.find(item => item.id === taskId)
    const subtask = task?.subtasks.find(item => item.id === subtaskId)
    if (!task || !subtask) return
    const descendantIds = new Set([subtaskId])
    let foundDescendant = true
    while (foundDescendant) {
      foundDescendant = false
      task.subtasks.forEach(item => {
        if (item.parentId && descendantIds.has(item.parentId) && !descendantIds.has(item.id)) {
          descendantIds.add(item.id)
          foundDescendant = true
        }
      })
    }
    const descendantCount = descendantIds.size - 1
    setConfirmation({
      title: 'Excluir tarefa?',
      description: descendantCount
        ? `A tarefa “${subtask.title}” e ${descendantCount} ${descendantCount === 1 ? 'subtarefa vinculada serão excluídas' : 'subtarefas vinculadas serão excluídas'} permanentemente. Esta ação não poderá ser desfeita.`
        : `A tarefa “${subtask.title}” será excluída permanentemente. Esta ação não poderá ser desfeita.`,
      confirmLabel: 'Excluir tarefa',
      onConfirm: () => deleteSubtask(taskId, subtaskId),
    })
  }
  function deleteSubtask(taskId: string, subtaskId: string) {
    const currentUser = currentUserInitials
    setTasks(current => current.map(task => {
      if (task.id !== taskId) return task
      const subtask = task.subtasks.find(item => item.id === subtaskId)
      if (!subtask) return task
      const removedIds = new Set([subtaskId])
      let foundDescendant = true
      while (foundDescendant) {
        foundDescendant = false
        task.subtasks.forEach(item => {
          if (item.parentId && removedIds.has(item.parentId) && !removedIds.has(item.id)) {
            removedIds.add(item.id)
            foundDescendant = true
          }
        })
      }
      const remainingSubtasks = task.subtasks.filter(item => !removedIds.has(item.id))
      const nextSubtasks = remainingSubtasks.map(item => item.status === 'Aprovada' && !dependencyState(remainingSubtasks, item).satisfied
        ? { ...item, status: 'Em espera' as TaskStatus, completedAt: undefined, analysisApprovedAt: undefined, analysisApprovedBy: undefined, running: false }
        : item)
      const reopenedTasks = remainingSubtasks.filter((item, index) => item.status === 'Aprovada' && nextSubtasks[index].status === 'Em espera')
      return {
        ...task,
        subtasks: nextSubtasks,
        activityEntries: [
          ...task.activityEntries,
          createActivityEntry(currentUser, subtask.status, `excluiu a ${subtask.parentId ? 'subtarefa' : 'tarefa'} “${subtask.title}”`),
          ...reopenedTasks.map(item => statusChangeActivity(
            currentUser, 'deliverable', item.id, item.title, 'Aprovada', 'Em espera',
            `reabriu automaticamente a tarefa “${item.title}” após a exclusão de “${subtask.title}”`,
          )),
        ],
      }
    }))
    setToast('Entrega excluída.')
  }
  function toggleSubtaskTimer(taskId: string, subtaskId: string) {
    const currentUser = currentUserInitials
    const selectedSubtask = tasks.find(task => task.id === taskId)?.subtasks.find(subtask => subtask.id === subtaskId)
    if (!selectedSubtask || (selectedSubtask.owner !== currentUser && !(selectedSubtask.supportUsers ?? []).includes(currentUser))) {
      setToast('Somente o responsável e os usuários de suporte podem controlar este cronômetro.')
      return
    }
    setTasksTransient(current => current.map(task => {
      if (task.id !== taskId) return task
      const selectedSubtask = task.subtasks.find(item => item.id === subtaskId)
      if (!selectedSubtask || (selectedSubtask.owner !== currentUser && !(selectedSubtask.supportUsers ?? []).includes(currentUser)) || selectedSubtask.status === 'Aprovada') return task
      const willRun = !userIsTimingSubtask(selectedSubtask, currentUser)
      const autoAdvanced = willRun && selectedSubtask.status === 'Em espera'
      const subtaskActivityEntry = autoAdvanced
        ? statusChangeActivity(currentUser, 'deliverable', selectedSubtask.id, selectedSubtask.title, selectedSubtask.status, 'Em execução')
        : createActivityEntry(currentUser, selectedSubtask.status, `${willRun ? 'iniciou' : 'pausou'} o próprio cronômetro na tarefa “${selectedSubtask.title}”`)
      const campaignStarted = willRun && task.status !== 'Em execução'
      const campaignActivityEntry = campaignStarted
        ? statusChangeActivity(
          currentUser, 'task', task.id, task.title, task.status, 'Em execução',
          `iniciou a campanha automaticamente ao dar play em “${selectedSubtask.title}”`,
        )
        : undefined
      return {
        ...task,
        status: campaignStarted ? 'Em execução' : task.status,
        subtasks: task.subtasks.map(item => {
          if (item.id !== subtaskId) return item
          const timing = toggleUserSubtaskTimer(item, currentUser, {
            id: crypto.randomUUID(), status: autoAdvanced ? 'Em execução' : item.status,
            startedAt: activityTimestamp(), startedOn: new Date().toISOString(),
          })
          return { ...item, running: timing.running, timeEntries: timing.timeEntries, status: autoAdvanced ? 'Em execução' : item.status }
        }),
        activityEntries: [...task.activityEntries, subtaskActivityEntry, ...(campaignActivityEntry ? [campaignActivityEntry] : [])],
      }
    }))
  }
  const closeTaskPanel = useCallback(() => {
    setSelectedTaskId(null)
    setHighlightedTaskId(null)
    if (campaignIdFromPath()) window.history.replaceState(null, '', pagePaths.tasks)
  }, [])
  const closeConfirmation = useCallback(() => setConfirmation(null), [])

  function openSystemNotification(notification: SystemNotification) {
    const readAt = new Date().toISOString()
    setNotifications(current => acknowledgeTaskNotifications(current, currentUserInitials, notification.taskId, readAt))
    setHighlightedTaskId(notification.taskId)
    openTaskPanel(notification.taskId)
  }

  function openTaskPanel(taskId: string) {
    const task = tasks.find(item => item.id === taskId)
    if (!task) return
    const hasUnreadNotification = notifications.some(notification => notification.recipient === currentUserInitials && notification.taskId === taskId && !notification.readAt)
    setHighlightedTaskId(hasUnreadNotification ? taskId : null)
    if (hasUnreadNotification) {
      const readAt = new Date().toISOString()
      setNotifications(current => acknowledgeTaskNotifications(current, currentUserInitials, taskId, readAt))
    }
    setPriorityHighlightedTaskIds(current => current.map(id => id === taskId ? '' : id))
    setPageState('tasks')
    setSelectedTaskId(taskId)
    const nextPath = campaignPath(task.campaignId)
    if (normalizedPath(window.location.pathname) !== nextPath) window.history.pushState(null, '', nextPath)
  }

  function markAllNotificationsRead() {
    const readAt = new Date().toISOString()
    setNotifications(current => current.map(notification => notification.recipient === currentUserInitials && !notification.readAt ? { ...notification, readAt } : notification))
  }

  function anchorSearchResultInKanban(taskId?: string) {
    setPage('tasks')
    setPriorityHighlightedTaskIds([])
    setHighlightedTaskId(taskId ?? null)
    if (!taskId) {
      setKanbanAnchor(null)
      return
    }
    const requestId = Date.now()
    setKanbanAnchor({ taskId, requestId })
    window.setTimeout(() => setKanbanAnchor(current => current?.requestId === requestId ? null : current), 2500)
  }

  if (authenticationLoading) return <main className="auth-loading" aria-label="Validando sessão"><Brand/><span/></main>
  if (!authenticated || !authenticatedUser) return <Login onAuthenticated={handleAuthenticated}/>
  if (!workspaceReady) return <main className="auth-loading" aria-label="Carregando dados"><Brand/><span/>{workspaceError && <><p>{workspaceError}</p><button type="button" onClick={() => window.location.reload()}>Tentar novamente</button></>}</main>

  const campaignAccessScope = hasFullCampaignClientAccess(role, currentUserProfile) ? { allClients: true, clients: {} } : activeAccessScope
  const visibleClients = clients.flatMap(client => {
    const scopedClient = clientForAccess(campaignAccessScope, client)
    return scopedClient ? [scopedClient] : []
  })
  const visibleCampaigns = campaigns.filter(campaign => campaignIsVisible(campaignAccessScope, campaign))
  const visibleCampaignIds = new Set(visibleCampaigns.map(campaign => campaign.id))
  const visibleTasks = tasks.filter(task => visibleCampaignIds.has(task.campaignId))
  const searchableKanbanTasks = visibleTasks.filter(task => role !== 'worker' || canOverrideStatus || task.status !== 'Backlog')
  const currentNotifications = notifications
    .filter(notification => notification.recipient === currentUserInitials)
    .sort((left, right) => right.createdOn.localeCompare(left.createdOn))
  const notifiedTaskIds = unreadTaskNotificationIds(currentNotifications)
  const context = {
    clients: visibleClients, campaigns: visibleCampaigns, tasks: visibleTasks, role, currentUser: currentUserInitials,
    currentUserName, currentUserProfile, accounts: accountDirectory, platformActivities, highlightedTaskId, kanbanAnchor,
    priorityHighlightedTaskIds, notifiedTaskIds, open: setModal, openTask: openTaskPanel, toggleDone, changeTaskStatus,
    moveTask: (id: string, status: TaskStatus, placement: TaskDropPlacement) => changeTaskStatus(id, status, placement),
    deleteCampaign, requestDeleteTask: requestDeleteCampaignTask,
    openCampaigns: () => setPage('tasks'),
    openPriorityCampaigns: () => {
      const priorityTaskIds = dashboardPriorityTasks(visibleTasks, currentUserInitials).map(task => task.id)
      setPage('tasks')
      setPriorityHighlightedTaskIds(priorityTaskIds)
    },
  }
  const selectedTask = visibleTasks.find(task => task.id === selectedTaskId)
  const selectedCampaign = visibleCampaigns.find(campaign => campaign.id === selectedTask?.campaignId)
  const canCreateTasksInSelectedCampaign = Boolean(selectedCampaign && canCreateTaskInCampaign({ profile: currentUserProfile, accessScope: activeAccessScope }, selectedCampaign))
  const selectedClient = visibleClients.find(client => client.id === selectedCampaign?.clientId)
  const selectedOwner = accountDirectory.find(user => user.initials === selectedTask?.owner)
  const selectedTaskAssignedUsers = new Set([
    selectedTask?.owner,
    ...(selectedTask?.subtasks.flatMap(subtask => [subtask.owner, subtask.reviewer, ...(subtask.supportUsers ?? [])]) ?? []),
  ])
  const selectedTaskUserNames = Object.fromEntries(accountDirectory.map(user => [user.initials, user.name]))
  const selectedTaskUserOptions = accountDirectory.flatMap(user =>
    !selectedCampaign || campaignIsVisible(user.accessScope, selectedCampaign) || selectedTaskAssignedUsers.has(user.initials)
      ? [{ value: user.initials, name: user.name, profile: user.profile, email: user.email, picture: user.picture }]
      : [],
  )
  const editingClient = clients.find(client => client.id === editingClientId)
  const canEditSelectedCampaignPanel = Boolean(selectedTask && canEditCampaignPanel(role, selectedTask.owner, currentUserInitials))
  const views: Record<Page, React.ReactNode> = {
    dashboard: <Dashboard {...context}/>, campaigns: <Campaigns key={role} {...context}/>, tasks: <Tasks key={role} {...context}/>, reports: <Reports key={role} campaigns={visibleCampaigns} tasks={visibleTasks} role={role} accounts={accountDirectory} currentUserProfile={currentUserProfile} currentUser={currentUserInitials} currentUserName={currentUserName}/>,
    talentDesk: canAccessTalentDesk(currentUserProfile) ? <TalentDesk googleClientId={authenticatedUser.googleClientId}/> : null,
    settings: <Settings role={role} clients={clients} accounts={accountDirectory} registeredAccounts={registeredAccounts} searchedAccountEmail={searchedAccountEmail} googleAccount={accountDirectory.find(account => account.email === authenticatedUser.email.toLowerCase())} avatarSyncing={avatarSyncing} onSyncGoogleAvatar={syncGoogleAvatar} open={setModal} onUpdateMockProfile={updateUserProfile} onUpdateMockLevel={updateUserLevel} onUpdateMockAccessScope={updateUserAccessScope} onUpdateRegisteredProfile={updateRegisteredProfile} onUpdateRegisteredLevel={updateRegisteredLevel} onUpdateRegisteredAccessScope={updateRegisteredAccessScope} onEditClient={client => setEditingClientId(client.id)} onDeleteClient={requestDeleteClient}/>,
  }

  return <><Layout page={page} role={role} simulatedUserKey={simulatedUserKey} userProfiles={userProfiles} currentUserProfile={currentUserProfile} notifications={currentNotifications} campaigns={visibleCampaigns} tasks={searchableKanbanTasks} accounts={accountDirectory} authenticatedUser={authenticatedUser} setPage={setPage} setTestUser={setTestUser} onOpenNotification={openSystemNotification} onMarkAllNotificationsRead={markAllNotificationsRead} onOpenSearchCampaign={campaignId => anchorSearchResultInKanban(searchableKanbanTasks.find(task => task.campaignId === campaignId)?.id)} onOpenSearchTask={taskId => anchorSearchResultInKanban(taskId)} onOpenSearchPerson={email => { setSearchedAccountEmail(email); setPage('settings'); window.setTimeout(() => setSearchedAccountEmail(current => current === email ? null : current), 3600) }} logout={logout}>{views[page]}</Layout>
    {modal === 'campaign' && canCreateCampaign && <CampaignModal clients={visibleClients} role={role} accounts={accountDirectory} currentUser={currentUserInitials} onClose={() => setModal(null)} onCreate={createCampaign}/>}
    {modal === 'client' && role === 'admin' && <ClientModal onClose={() => setModal(null)} onCreate={client => { setClients(current => [...current, client]); setModal(null); setToast('Cliente adicionado com sucesso.') }}/>} 
    {editingClient && role === 'admin' && <ClientManagementModal client={editingClient} onClose={() => setEditingClientId(null)} onSave={updateClient}/>}
    {selectedTask && <TaskPanel
      task={selectedTask}
      campaign={selectedCampaign}
      client={selectedClient}
      ownerName={selectedOwner?.name ?? selectedTask.owner}
      userNames={selectedTaskUserNames}
      userOptions={selectedTaskUserOptions}
      currentUser={currentUserInitials}
      canManageTask={canEditSelectedCampaignPanel}
      canOverrideStatus={canOverrideStatus}
      canMoveToReview={canMoveCampaignToReview(selectedTask, currentUserProfile)}
      canAssignCampaignOwner={role !== 'worker' || selectedCampaign?.createdBy === currentUserInitials}
      canAssignOwner={role !== 'worker'}
      canEditTaskNames={role !== 'worker'}
      canEditSubtasks={role !== 'worker'}
      canCreateSubtasks={canCreateTasksInSelectedCampaign}
      canDeleteSubtasks={role !== 'worker'}
      canViewRegisteredTime={role !== 'worker'}
      canViewTimeHistory={role !== 'worker'}
      canWithdrawAnalysisApproval={canWithdrawCampaignTaskApproval(currentUserProfile)}
      onClose={closeTaskPanel}
      onUpdate={changes => updateTask(selectedTask.id, changes)}
      onChangeStatus={status => changeTaskStatus(selectedTask.id, status)}
      onApproveSubtasks={subtaskIds => approveCampaignSubtasks(selectedTask.id, subtaskIds)}
      onWithdrawSubtaskApproval={subtaskId => requestWithdrawCampaignSubtaskApproval(selectedTask.id, subtaskId)}
      onRequestAlterations={items => requestCampaignAlterations(selectedTask.id, items)}
      onAddSubtask={(materialCategory, material, volume, pages) => addSubtask(selectedTask.id, materialCategory, material, volume, pages)}
      onAddChildSubtask={(parentId, title, kind, volume, pages) => addChildSubtask(selectedTask.id, parentId, title, kind, volume, pages)}
      onUpdateSubtask={(subtaskId, changes) => updateSubtask(selectedTask.id, subtaskId, changes)}
      onDuplicateSubtask={subtaskId => duplicateSubtask(selectedTask.id, subtaskId)}
      onDeleteSubtask={subtaskId => requestDeleteSubtask(selectedTask.id, subtaskId)}
      onToggleSubtaskTimer={subtaskId => toggleSubtaskTimer(selectedTask.id, subtaskId)}
    />}
    {confirmation && <ConfirmDialog
      title={confirmation.title}
      description={confirmation.description}
      confirmLabel={confirmation.confirmLabel}
      cancelLabel={confirmation.cancelLabel}
      tone={confirmation.tone}
      onCancel={closeConfirmation}
      onConfirm={() => { const action = confirmation.onConfirm; setConfirmation(null); action() }}
    />}
    <div className={`toast ${toast ? 'show' : ''}`} role="status">{toast}</div>
  </>
}

interface WorkProps {
  clients: Client[]; campaigns: Campaign[]; tasks: Task[]; role: Role
  currentUser: string; currentUserName: string; currentUserProfile: UserProfile; accounts: RegisteredAccount[]
  platformActivities: PlatformActivity[]
  highlightedTaskId: string | null
  kanbanAnchor: { taskId: string; requestId: number } | null
  priorityHighlightedTaskIds: string[]
  notifiedTaskIds: string[]
  open: (modal: Modal) => void; openTask: (id: string) => void; toggleDone: (id: string) => void
  changeTaskStatus: (id: string, status: TaskStatus) => void
  moveTask: (id: string, status: TaskStatus, placement: TaskDropPlacement) => void
  deleteCampaign: (id: string) => void
  requestDeleteTask: (id: string) => void
  openCampaigns: () => void
  openPriorityCampaigns: () => void
}

function CampaignRows({ campaigns, clients, accounts, limit, canDelete = false, onDelete }: { campaigns: Campaign[]; clients: Client[]; accounts: RegisteredAccount[]; limit?: number; canDelete?: boolean; onDelete?: (id: string) => void }) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const openActions = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openMenuId) return

    function closeOutside(event: MouseEvent) {
      if (event.target instanceof Node && !openActions.current?.contains(event.target)) setOpenMenuId(null)
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenMenuId(null)
    }

    document.addEventListener('mousedown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [openMenuId])

  return <>{campaigns.slice(0, limit).map(campaign => { const client = clients.find(item => item.id === campaign.clientId); return <div className="project-row" key={campaign.id}><div className="project-main"><span className={`project-dot ${client?.logo ? 'has-image' : ''}`}>{client?.logo ? <img src={client.logo} alt={`Logo de ${client.name}`}/> : campaign.code}</span><div><b>{campaign.name}</b><small>{client?.name}{campaign.operation && ` · ${campaign.operation}`}{campaign.reportArea && ` · ${campaign.reportArea}`}</small></div></div><div className="progress-wrap"><div className="progress"><i style={{ width: `${campaign.progress}%` }}/></div><small>{campaign.progress}% concluído</small></div><div className="team">{campaign.members.map(member => { const account = accounts.find(item => item.initials === member); return <UserAvatar initials={member} name={account?.name} picture={account?.picture} className="mini-avatar" key={member}/> })}</div>{canDelete && <div className="campaign-actions" ref={openMenuId === campaign.id ? openActions : undefined}><button type="button" className="more" aria-label={`Ações da campanha ${campaign.name}`} aria-haspopup="menu" aria-expanded={openMenuId === campaign.id} onClick={() => setOpenMenuId(current => current === campaign.id ? null : campaign.id)}>⋯</button>{openMenuId === campaign.id && <div className="campaign-menu" role="menu"><button type="button" role="menuitem" onClick={() => { setOpenMenuId(null); onDelete?.(campaign.id) }}><Icon name="trash"/> Excluir campanha</button></div>}</div>}</div> })}</>
}

function Dashboard({ clients, campaigns, tasks, role, currentUser, currentUserName, currentUserProfile, accounts, platformActivities, open, openTask, toggleDone, openCampaigns, openPriorityCampaigns }: WorkProps) {
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null)
  const completionTimer = useRef<number | null>(null)
  const first = currentUserName.split(' ')[0]
  const taskActivities = tasks.flatMap((task, taskIndex) => {
    const campaign = campaigns.find(item => item.id === task.campaignId)
    return task.activityEntries.map((entry, entryIndex) => ({ id: entry.id, user: entry.user, description: entry.description, context: `${campaign?.name ?? 'Campanha'} · ${task.title}`, occurredAt: entry.occurredAt, occurredOn: entry.occurredOn, statusChange: entry.statusChange, statusEntity: entry.statusChange?.entity, order: taskIndex * 1000 + entryIndex }))
  }).filter(item => role !== 'worker' || item.statusEntity === 'task')
  const recentActivities = [...taskActivities, ...platformActivities.map((entry, index) => ({ ...entry, statusChange: undefined, statusEntity: 'platform', order: 1_000_000 + index }))]
    .sort((a, b) => (b.occurredOn ?? '').localeCompare(a.occurredOn ?? '') || b.order - a.order)
    .slice(0, 5)

  const accountNames = Object.fromEntries(accounts.map(account => [account.initials, account.name]))
  const priorityTasks = dashboardPriorityTasks(tasks, currentUser)
  useEffect(() => () => {
    if (completionTimer.current !== null) window.clearTimeout(completionTimer.current)
  }, [])

  function completeFromQueue(id: string) {
    if (completingTaskId) return
    setCompletingTaskId(id)
    completionTimer.current = window.setTimeout(() => {
      toggleDone(id)
      setCompletingTaskId(null)
      completionTimer.current = null
    }, 620)
  }

  return <><PageHead title={`Olá, ${first}!`} subtitle={role === 'manager' ? 'Gerencie campanhas, clientes e o ritmo de entrega do time.' : 'Visão geral do seu workflow:'} action={canCreateCampaignForProfile(role, currentUserProfile) ? 'Nova campanha' : undefined} onAction={() => open('campaign')}/><div className="dashboard-grid"><div className="card section-card dashboard-card dashboard-campaigns"><div className="section-head dashboard-section-head"><div><span>Agora</span><h3>Campanhas em destaque</h3><p>O que está em movimento no time.</p></div><button type="button" onClick={openCampaigns}>Ver todas <b aria-hidden="true">→</b></button></div><CampaignRows campaigns={campaigns} clients={clients} accounts={accounts} limit={3}/></div><div className="card section-card dashboard-card dashboard-tasks"><div className="section-head dashboard-section-head"><div><span>Sua fila</span><h3>Minhas próximas tarefas</h3><p>Comece pelo que pede atenção primeiro.</p></div><button type="button" onClick={openPriorityCampaigns}>Ver todas <b aria-hidden="true">→</b></button></div>{priorityTasks.map(task => { const campaign=campaigns.find(c=>c.id===task.campaignId); const client=clients.find(c=>c.id===campaign?.clientId); const taskOwnerName=accounts.find(user=>user.initials===task.owner)?.name ?? task.owner; const completionReason = currentUserProfile !== 'Administrador' && role === 'worker' && task.owner !== currentUser ? `Esta tarefa está atribuída a ${taskOwnerName}. Somente o responsável pode ${task.status === 'Aprovada' ? 'reabrir sua conclusão' : 'marcá-la como feita'}.` : undefined; const isCompleting = completingTaskId === task.id; return <div className={`task-row priority-${task.priority.toLowerCase().replace('é','e')} ${isCompleting ? 'is-completing' : ''}`} aria-busy={isCompleting} key={task.id}><div className="task-line"><ActionTooltip reason={completionReason} className="dashboard-check-tooltip"><button className={`check ${isCompleting ? 'done' : ''}`} disabled={Boolean(completionReason) || Boolean(completingTaskId)} onClick={()=>completeFromQueue(task.id)} aria-label={`Marcar ${task.title} como feita`}>{isCompleting && <Icon name="check"/>}</button></ActionTooltip><button className={`task-title task-title-link ${isCompleting ? 'done' : ''}`} disabled={isCompleting} onClick={() => openTask(task.id)}>{task.title}</button><span className={`tag ${task.priority==='Alta'?'high':''}`}>{isCompleting ? 'Concluída' : task.priority}</span></div><div className="task-bottom"><div className="task-meta">{client?.name} · {campaign?.name} · {task.due}</div></div></div>})}{!priorityTasks.length && <div className="dashboard-empty"><Icon name="check"/><b>Fila em dia</b><span>Nenhuma tarefa pendente por aqui.</span></div>}</div></div><div className="card section-card dashboard-card dashboard-activity" style={{marginTop:16}}><div className="section-head dashboard-section-head"><div><span>Nos bastidores</span><h3>Atividade recente</h3><p>As últimas movimentações da equipe.</p></div></div>{recentActivities.map(entry => { const account = accounts.find(item => item.initials === entry.user); const description = campaignStatusActivityDescription(entry); return <div className="activity" key={entry.id}><UserAvatar initials={entry.user} name={account?.name} picture={account?.picture} className="mini-avatar"/><div><p><b>{account?.name ?? entry.user}</b> {expandCollaboratorInitials(description, accountNames)}.</p><small>{entry.context}</small><time>{entry.occurredAt}</time></div></div> })}{!recentActivities.length && <div className="activity-empty">Nenhuma alteração de status da campanha registrada.</div>}</div></>
}

function Campaigns({ campaigns, clients, role, currentUserProfile, accounts, open, deleteCampaign }: WorkProps) {
  const [clientFilter, setClientFilter] = useState('all')
  const [operationFilter, setOperationFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const availableOperations = useMemo(() => {
    const source = clientFilter === 'all' ? clients : clients.filter(client => client.id === clientFilter)
    return [...new Set(source.flatMap(client => client.operations))]
  }, [clientFilter, clients])
  const filteredCampaigns = useMemo(() => campaigns.filter(campaign =>
    (clientFilter === 'all' || campaign.clientId === clientFilter) &&
    (operationFilter === 'all' || campaign.operation === operationFilter) &&
    (statusFilter === 'all' || campaign.status === statusFilter)
  ), [campaigns, clientFilter, operationFilter, statusFilter])

  function changeClient(value: string) {
    setClientFilter(value)
    if (operationFilter !== 'all') {
      const nextClient = clients.find(client => client.id === value)
      if (value !== 'all' && !nextClient?.operations.includes(operationFilter)) setOperationFilter('all')
    }
  }

  return <><PageHead title="Campanhas" subtitle="Acompanhe as campanhas por cliente e operação." action={canCreateCampaignForProfile(role, currentUserProfile) ? 'Nova campanha' : undefined} onAction={() => open('campaign')}/><div className="toolbar">
    <select className="filter" aria-label="Filtrar por cliente" value={clientFilter} onChange={event => changeClient(event.target.value)}><option value="all">Todos os clientes</option>{clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}</select>
    <select className="filter" aria-label="Filtrar por operação" value={operationFilter} onChange={event => setOperationFilter(event.target.value)}><option value="all">Todas as operações</option>{availableOperations.map(operation => <option key={operation}>{operation}</option>)}</select>
    <select className="filter" aria-label="Filtrar por status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="all">Todos os status</option><option>Em andamento</option><option>Pausada</option><option>Concluída</option></select>
    {(clientFilter !== 'all' || operationFilter !== 'all' || statusFilter !== 'all') && <button className="clear-filters" onClick={() => { setClientFilter('all'); setOperationFilter('all'); setStatusFilter('all') }}>Limpar filtros</button>}
  </div><div className="card section-card">{filteredCampaigns.length ? <CampaignRows campaigns={filteredCampaigns} clients={clients} accounts={accounts} canDelete={role !== 'worker'} onDelete={deleteCampaign}/> : <div className="empty-filter"><Icon name="search"/><b>Nenhuma campanha encontrada</b><span>Tente ajustar ou limpar os filtros selecionados.</span></div>}</div></>
}

export function Tasks({ clients, campaigns, tasks, role, currentUser, currentUserProfile, accounts, highlightedTaskId, kanbanAnchor, priorityHighlightedTaskIds, notifiedTaskIds, open, openTask, moveTask, requestDeleteTask }: WorkProps) {
  const [campaignFilter, setCampaignFilter] = useState('all')
  const [clientFilter, setClientFilter] = useState('all')
  const [operationFilter, setOperationFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'mine'>('all')
  const [view, setView] = useState<'board' | 'list'>('board')
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null)
  const [glowingColumn, setGlowingColumn] = useState<TaskStatus | null>(null)
  const [dropPosition, setDropPosition] = useState<{ status: TaskStatus; beforeTaskId: string | null } | null>(null)
  const [openTaskMenuId, setOpenTaskMenuId] = useState<string | null>(null)
  const kanbanRef = useRef<HTMLDivElement>(null)
  const [kanbanScroll, setKanbanScroll] = useState({ left: 0, max: 0 })

  useEffect(() => {
    if (!highlightedTaskId && !kanbanAnchor) return
    setCampaignFilter('all')
    setClientFilter('all')
    setOperationFilter('all')
    setPriorityFilter('all')
    setAssignmentFilter('all')
    if (kanbanAnchor) setView('board')
  }, [highlightedTaskId, kanbanAnchor])

  useEffect(() => {
    if (!canCreateCampaignForProfile(role, currentUserProfile)) return
    function openNewCampaignFromKeyboard(event: KeyboardEvent) {
      if (!isNewCampaignShortcut(event)) return
      event.preventDefault()
      open('campaign')
    }
    document.addEventListener('keydown', openNewCampaignFromKeyboard)
    return () => document.removeEventListener('keydown', openNewCampaignFromKeyboard)
  }, [currentUserProfile, open, role])

  useEffect(() => {
    if (!openTaskMenuId) return
    function closeTaskMenu(event: MouseEvent) {
      if (event.target instanceof Element && event.target.closest('.task-card-actions')) return
      setOpenTaskMenuId(null)
    }
    function closeTaskMenuOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenTaskMenuId(null)
    }
    document.addEventListener('mousedown', closeTaskMenu)
    document.addEventListener('keydown', closeTaskMenuOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeTaskMenu)
      document.removeEventListener('keydown', closeTaskMenuOnEscape)
    }
  }, [openTaskMenuId])

  const canOverrideStatus = hasUnrestrictedStatusAccess(currentUserProfile)
  const groups: Array<[TaskStatus, string]> = [
    ...(role !== 'worker' || canOverrideStatus ? [['Backlog', 'BACKLOG'] as [TaskStatus, string]] : []),
    ['Em espera', 'EM ESPERA'], ['Em execução', 'EM EXECUÇÃO'], ['Em alteração', 'EM ALTERAÇÃO'],
    ['Em análise', 'EM ANÁLISE'], ['Aprovada', 'APROVADA INTERNAMENTE'], ['Reprovada', 'REPROVADA PELO CLIENTE'],
  ]
  const belongsToCurrentUser = (task: Task, campaign?: Campaign) => campaign?.members.includes(currentUser) === true || task.owner === currentUser || task.subtasks.some(subtask => subtask.owner === currentUser || subtask.reviewer === currentUser || (subtask.supportUsers ?? []).includes(currentUser))
  const availableOperations = useMemo(() => [...new Set(campaigns.filter(campaign => clientFilter === 'all' || campaign.clientId === clientFilter).map(campaign => campaign.operation).filter((operation): operation is string => Boolean(operation)))], [campaigns, clientFilter])
  const availableCampaigns = useMemo(() => campaigns.filter(campaign =>
    (clientFilter === 'all' || campaign.clientId === clientFilter) &&
    (operationFilter === 'all' || campaign.operation === operationFilter) &&
    (assignmentFilter === 'all' || campaign.members.includes(currentUser) || tasks.some(task => task.campaignId === campaign.id && belongsToCurrentUser(task, campaign)))
  ), [assignmentFilter, campaigns, clientFilter, currentUser, operationFilter, tasks])
  const filteredTasks = useMemo(() => tasks.filter(task => {
    const campaign = campaigns.find(item => item.id === task.campaignId)
    return (role !== 'worker' || canOverrideStatus || task.status !== 'Backlog') &&
      (assignmentFilter === 'all' || belongsToCurrentUser(task, campaign)) &&
      (campaignFilter === 'all' || task.campaignId === campaignFilter) &&
      (clientFilter === 'all' || campaign?.clientId === clientFilter) &&
      (operationFilter === 'all' || campaign?.operation === operationFilter) &&
      (priorityFilter === 'all' || task.priority === priorityFilter)
  }), [assignmentFilter, campaignFilter, campaigns, canOverrideStatus, clientFilter, currentUser, operationFilter, priorityFilter, role, tasks])

  useEffect(() => {
    if (view !== 'board') return
    const board = kanbanRef.current
    if (!board) return
    const updateScrollPosition = () => setKanbanScroll({
      left: board.scrollLeft,
      max: Math.max(0, board.scrollWidth - board.clientWidth),
    })
    updateScrollPosition()
    board.addEventListener('scroll', updateScrollPosition, { passive: true })
    window.addEventListener('resize', updateScrollPosition)
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateScrollPosition)
    observer?.observe(board)
    Array.from(board.children).forEach(child => observer?.observe(child))
    return () => {
      board.removeEventListener('scroll', updateScrollPosition)
      window.removeEventListener('resize', updateScrollPosition)
      observer?.disconnect()
    }
  }, [filteredTasks.length, role, view])

  useEffect(() => {
    if (!kanbanAnchor || view !== 'board' || !filteredTasks.some(task => task.id === kanbanAnchor.taskId)) return
    let secondFrame = 0
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const card = document.querySelector<HTMLElement>(`.kanban [data-task-id="${CSS.escape(kanbanAnchor.taskId)}"]`)
        card?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
      })
    })
    return () => {
      window.cancelAnimationFrame(firstFrame)
      window.cancelAnimationFrame(secondFrame)
    }
  }, [filteredTasks, kanbanAnchor, view])

  function changeClient(value: string) {
    setClientFilter(value)
    if (operationFilter !== 'all') {
      const operations = campaigns.filter(campaign => value === 'all' || campaign.clientId === value).map(campaign => campaign.operation)
      if (!operations.includes(operationFilter)) setOperationFilter('all')
    }
    if (campaignFilter !== 'all') {
      const selectedCampaign = campaigns.find(campaign => campaign.id === campaignFilter)
      if (value !== 'all' && selectedCampaign?.clientId !== value) setCampaignFilter('all')
    }
  }

  function changeOperation(value: string) {
    setOperationFilter(value)
    if (campaignFilter !== 'all') {
      const selectedCampaign = campaigns.find(campaign => campaign.id === campaignFilter)
      if (value !== 'all' && selectedCampaign?.operation !== value) setCampaignFilter('all')
    }
  }

  const hasFilters = campaignFilter !== 'all' || clientFilter !== 'all' || operationFilter !== 'all' || priorityFilter !== 'all' || assignmentFilter !== 'all'
  const canManageFlow = role !== 'worker' || canOverrideStatus

  function moveDraggedTask(status: TaskStatus) {
    if (canManageFlow && draggedTaskId) {
      const beforeTaskId = dropPosition?.status === status ? dropPosition.beforeTaskId : null
      moveTask(draggedTaskId, status, { beforeTaskId })
    }
    setDraggedTaskId(null)
    setGlowingColumn(null)
    setDropPosition(null)
  }

  function updateDragFeedback(event: React.DragEvent<HTMLDivElement>) {
    if (!canManageFlow || !draggedTaskId) return
    event.preventDefault()
    const columns = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-drop-status]')].map(element => {
      const rect = element.getBoundingClientRect()
      return { element, status: element.dataset.dropStatus as TaskStatus, left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom }
    })
    const hoveredColumn = columns.find(column => event.clientX >= column.left && event.clientX <= column.right && event.clientY >= column.top && event.clientY <= column.bottom)?.element
    setGlowingColumn(dragGlowStatus(columns, event.clientX, event.clientY))
    if (!hoveredColumn) {
      setDropPosition(null)
      return
    }
    const status = hoveredColumn.dataset.dropStatus as TaskStatus
    const cardElements = [...hoveredColumn.querySelectorAll<HTMLElement>('[data-task-id]')]
      .filter(card => card.dataset.taskId !== draggedTaskId)
    const beforeCard = cardElements.find(card => event.clientY < card.getBoundingClientRect().top + card.getBoundingClientRect().height / 2)
    setDropPosition({ status, beforeTaskId: beforeCard?.dataset.taskId ?? null })
  }

  function subtaskSummary(task: Task) {
    const completed = task.subtasks.filter(subtask => subtask.status === 'Aprovada').length
    return { completed, total: task.subtasks.length }
  }

  function moveKanbanViewport(direction: -1 | 1) {
    const board = kanbanRef.current
    if (!board) return
    board.scrollBy({ left: direction * Math.max(280, board.clientWidth * .72), behavior: 'smooth' })
  }

  return <><PageHead title="Kanban das campanhas" subtitle="Ferramenta visual de gerenciamento que ajuda a organizar tarefas, controlar o fluxo de trabalho e aumentar a produtividade" action={canCreateCampaignForProfile(role, currentUserProfile) ? 'Nova campanha' : undefined} actionShortcut={canCreateCampaignForProfile(role, currentUserProfile) ? 'N' : undefined} onAction={() => open('campaign')}/><div className="tasks-view-bar" aria-label="Visualização das tarefas">
    <button className={view === 'board' ? 'active' : ''} type="button" onClick={() => setView('board')}><Icon name="grid"/> Quadro</button>
    <button className={view === 'list' ? 'active' : ''} type="button" onClick={() => setView('list')}><Icon name="list"/> Lista</button>
  </div><div className="toolbar">
    <select className="filter" aria-label="Filtrar por campanha" value={campaignFilter} onChange={event => setCampaignFilter(event.target.value)}><option value="all">Todas as campanhas</option>{availableCampaigns.map(campaign => <option key={campaign.id} value={campaign.id}>{campaign.name}</option>)}</select>
    <select className="filter" aria-label="Filtrar por cliente" value={clientFilter} onChange={event => changeClient(event.target.value)}><option value="all">Todos os clientes</option>{clients.map(client => <option key={client.id} value={client.id}>{client.name}</option>)}</select>
    <select className="filter" aria-label="Filtrar por operação regional" value={operationFilter} onChange={event => changeOperation(event.target.value)}><option value="all">Todas as operações</option>{availableOperations.map(operation => <option key={operation}>{operation}</option>)}</select>
    <select className="filter" aria-label="Filtrar por prioridade" value={priorityFilter} onChange={event => setPriorityFilter(event.target.value)}><option value="all">Todas as prioridades</option><option>Alta</option><option>Média</option><option>Baixa</option></select>
    <select className="filter" aria-label="Filtrar por participação" value={assignmentFilter} onChange={event => { setAssignmentFilter(event.target.value as 'all' | 'mine'); setCampaignFilter('all') }}><option value="all">Toda a equipe</option><option value="mine">Onde sou citado/responsável</option></select>
    {hasFilters && <button className="clear-filters" onClick={() => { setCampaignFilter('all'); setClientFilter('all'); setOperationFilter('all'); setPriorityFilter('all'); setAssignmentFilter('all') }}>Limpar filtros</button>}
  </div>{view === 'board' ? <><div className={`kanban-scroll-control ${kanbanScroll.max <= 0 ? 'is-disabled' : ''}`} aria-label="Navegação horizontal do Kanban">
    <button type="button" onClick={() => moveKanbanViewport(-1)} disabled={kanbanScroll.left <= 1} aria-label="Navegar para as colunas à esquerda">‹</button>
    <input type="range" min="0" max={Math.max(1, kanbanScroll.max)} step="1" value={Math.min(kanbanScroll.left, Math.max(1, kanbanScroll.max))} onChange={event => { if (kanbanRef.current) kanbanRef.current.scrollLeft = Number(event.target.value) }} aria-label="Posição horizontal no Kanban"/>
    <button type="button" onClick={() => moveKanbanViewport(1)} disabled={kanbanScroll.max <= 0 || kanbanScroll.left >= kanbanScroll.max - 1} aria-label="Navegar para as colunas à direita">›</button>
  </div><div ref={kanbanRef} className={`kanban ${canManageFlow ? 'manager-flow' : ''}`} onDragOver={updateDragFeedback} onDragLeave={event => { const rect = event.currentTarget.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) { setGlowingColumn(null); setDropPosition(null) } }}>{groups.map(([status, label]) => {
    const items = filteredTasks.filter(task => task.status === status)
    const statusSlug = status.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replaceAll(' ', '-')
    return <div className={`column status-${statusSlug} ${draggedTaskId ? 'drag-active' : ''} ${glowingColumn === status ? 'drag-proximity' : ''} ${dropPosition?.status === status && dropPosition.beforeTaskId === null ? 'drop-at-end' : ''}`} data-drop-status={status} key={status} onDrop={() => moveDraggedTask(status)}>
      <div className="column-head"><span>{label}</span><b>{items.length}</b></div>
      {items.map(task => {
        const campaign = campaigns.find(item => item.id === task.campaignId)
        const client = clients.find(item => item.id === campaign?.clientId)
        const progress = subtaskSummary(task)
        const owner = accounts.find(account => account.initials === task.owner)
        const priorityAttackRank = priorityHighlightedTaskIds.indexOf(task.id) + 1
        const notificationHighlighted = task.id === highlightedTaskId || notifiedTaskIds.includes(task.id)
        const notified = priorityAttackRank > 0 || notificationHighlighted
        const canDelete = canDeleteCampaignTask(currentUserProfile, currentUser, campaign)
        return <div className={`task-card-shell ${draggedTaskId === task.id ? 'is-dragging-source' : ''} ${dropPosition?.status === status && dropPosition.beforeTaskId === task.id ? 'drop-before' : ''}`} data-task-id={task.id} key={task.id}>
          <article className={`kanban-card interactive ${canDelete ? 'has-actions' : ''} ${notified ? 'task-notification-glow' : ''} ${draggedTaskId === task.id ? 'is-dragging' : ''}`} role="button" tabIndex={0} draggable={canManageFlow} aria-label={`Abrir painel da campanha ${task.title}${priorityAttackRank > 0 ? `. Prioridade de ataque ${priorityAttackRank}` : notificationHighlighted ? '. Campanha indicada por uma notificação não lida' : ''}`} title={canManageFlow ? 'Arraste para mudar a etapa' : undefined} onDragStart={event => { event.dataTransfer.effectAllowed = 'move'; setOpenTaskMenuId(null); setDraggedTaskId(task.id) }} onDragEnd={() => { setDraggedTaskId(null); setGlowingColumn(null); setDropPosition(null) }} onClick={() => openTask(task.id)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openTask(task.id) } }}>
            {priorityAttackRank > 0 && <span className="priority-attack-rank" aria-label={`Prioridade de ataque ${priorityAttackRank}`}>{priorityAttackRank}</span>}
            <div className="kanban-card-top"><span className={`tag ${task.priority === 'Alta' ? 'high' : ''}`}>{task.priority}</span><span className="client-pill">{client?.name}</span></div>
            <h4>{task.title}</h4>
            <div className="campaign-name">{campaign?.name}{campaign?.operation && ` · ${campaign.operation}`}</div>
            {progress.total > 0 && <div className="subtask-progress"><div><i style={{ width: `${progress.completed / progress.total * 100}%` }}/></div><span><Icon name="check"/>{progress.completed}/{progress.total} entregas</span></div>}
            <div className="kanban-footer"><UserAvatar initials={task.owner} name={owner?.name} picture={owner?.picture} className="mini-avatar"/></div>
          </article>
          {canDelete && <div className="campaign-actions task-card-actions">
            <button type="button" className="more" aria-label={`Ações da tarefa ${task.title}`} aria-haspopup="menu" aria-expanded={openTaskMenuId === task.id} onClick={() => setOpenTaskMenuId(current => current === task.id ? null : task.id)}>⋯</button>
            {openTaskMenuId === task.id && <div className="campaign-menu" role="menu"><button type="button" role="menuitem" onClick={() => { setOpenTaskMenuId(null); requestDeleteTask(task.id) }}><Icon name="trash"/> Excluir</button></div>}
          </div>}
        </div>
      })}
    </div>
  })}</div></> : <div className="card task-list-view">
    <div className="task-list-row task-list-head"><span>Tarefa</span><span>Campanha</span><span>Responsável</span><span>Etapa</span><span>Entregas</span><span>Tempo</span><span>Prazo</span><span>Ações</span></div>
    {filteredTasks.map(task => {
      const campaign = campaigns.find(item => item.id === task.campaignId)
      const progress = subtaskSummary(task)
      const owner = accounts.find(account => account.initials === task.owner)
      const totalSeconds = task.subtasks.reduce((sum, subtask) => sum + subtask.seconds, 0)
      const notified = task.id === highlightedTaskId || priorityHighlightedTaskIds.includes(task.id) || notifiedTaskIds.includes(task.id)
      const canDelete = canDeleteCampaignTask(currentUserProfile, currentUser, campaign)
      return <div className={`task-list-row interactive ${notified ? 'task-notification-glow' : ''}`} key={task.id} role="button" tabIndex={0} onClick={() => openTask(task.id)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openTask(task.id) } }} aria-label={`Abrir painel da campanha ${task.title}${notified ? '. Campanha indicada por uma notificação não lida' : ''}`}>
        <span className="task-list-title"><b>{task.title}</b><small>{task.priority}</small></span><span className="task-list-campaign">{campaign?.name}<small>{campaign?.operation}</small></span><span className="task-owner-compact"><UserAvatar initials={task.owner} name={owner?.name} picture={owner?.picture} className="task-owner-avatar"/>{owner?.name ?? task.owner}</span><span className={`demand-status status-${task.status.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replaceAll(' ', '-')}`}>{campaignStatusLabel(task.status)}</span><span>{progress.total ? `${progress.completed}/${progress.total}` : '—'}</span><span className="list-time">{formatTime(totalSeconds)}</span><span>{task.due}</span><span className="task-list-actions">{canDelete && <button type="button" aria-label={`Excluir tarefa ${task.title}`} onClick={event => { event.stopPropagation(); requestDeleteTask(task.id) }}><Icon name="trash"/></button>}</span>
      </div>
    })}
    {!filteredTasks.length && <div className="empty-filter"><Icon name="search"/><b>Nenhuma tarefa encontrada</b><span>Ajuste os filtros para visualizar outras demandas.</span></div>}
  </div>}</>
}

function Settings({ role, clients, accounts, registeredAccounts, searchedAccountEmail, googleAccount, avatarSyncing, onSyncGoogleAvatar, open, onUpdateMockProfile, onUpdateMockLevel, onUpdateMockAccessScope, onUpdateRegisteredProfile, onUpdateRegisteredLevel, onUpdateRegisteredAccessScope, onEditClient, onDeleteClient }: { role: Role; clients: Client[]; accounts: RegisteredAccount[]; registeredAccounts: RegisteredAccount[]; searchedAccountEmail: string | null; googleAccount?: RegisteredAccount; avatarSyncing: boolean; onSyncGoogleAvatar: () => void; open: (modal: Modal) => void; onUpdateMockProfile: (accountRole: UserKey, profile: UserProfile) => void; onUpdateMockLevel: (accountRole: UserKey, level: UserLevel) => void; onUpdateMockAccessScope: (accountRole: UserKey, scope: UserAccessScope) => void; onUpdateRegisteredProfile: (email: string, profile: UserProfile) => void; onUpdateRegisteredLevel: (email: string, level: UserLevel) => void; onUpdateRegisteredAccessScope: (email: string, scope: UserAccessScope) => void; onEditClient: (client: Client) => void; onDeleteClient: (client: Client) => void }) {
  useEffect(() => {
    function closeClientMenus(event: PointerEvent) {
      document.querySelectorAll<HTMLDetailsElement>('.client-actions[open]').forEach(menu => {
        if (!(event.target instanceof Node) || !menu.contains(event.target)) menu.removeAttribute('open')
      })
    }
    function closeClientMenusWithKeyboard(event: KeyboardEvent) {
      if (event.key === 'Escape') document.querySelectorAll<HTMLDetailsElement>('.client-actions[open]').forEach(menu => menu.removeAttribute('open'))
    }
    document.addEventListener('pointerdown', closeClientMenus)
    document.addEventListener('keydown', closeClientMenusWithKeyboard)
    return () => {
      document.removeEventListener('pointerdown', closeClientMenus)
      document.removeEventListener('keydown', closeClientMenusWithKeyboard)
    }
  }, [])

  useEffect(() => {
    if (!searchedAccountEmail) return
    window.requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-account-email="${CSS.escape(searchedAccountEmail)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }))
  }, [searchedAccountEmail])

  const registeredEmails = new Set(registeredAccounts.map(account => account.email))

  return <><PageHead title="Configurações" subtitle="Administre o ambiente StarTracker."/>{role === 'admin' ? <>
    <div className="card section-card"><div className="section-head"><div><h3>Clientes e operações</h3><p className="section-description">Use o menu de cada cliente para editar ou excluir seus dados e operações.</p></div><button className="primary compact" onClick={()=>open('client')}><Icon name="plus"/> Novo cliente</button></div><div className="client-grid">{clients.map(client=><article className="client-card" key={client.id}><div className="client-card-head"><span className={`client-logo ${client.logo ? 'has-image' : ''}`}>{client.logo ? <img src={client.logo} alt={`Logo de ${client.name}`}/> : client.name.slice(0,2).toUpperCase()}</span><div><b>{client.name}</b><small>{client.operations.length} {client.operations.length === 1 ? 'operação' : 'operações'}</small></div><details className="client-actions"><summary className="more" aria-label={`Ações do cliente ${client.name}`}>⋯</summary><div className="client-menu"><button type="button" onClick={event=>{ event.currentTarget.closest('details')?.removeAttribute('open'); onEditClient(client) }}>Editar cliente e operações</button><button type="button" className="danger" onClick={event=>{ event.currentTarget.closest('details')?.removeAttribute('open'); onDeleteClient(client) }}><Icon name="trash"/>Excluir cliente</button></div></details></div><div className="operation-list">{client.operations.length ? client.operations.map(operation=><span key={operation}>{operation}</span>) : <em>Nenhuma operação cadastrada</em>}</div></article>)}</div></div>
    <div className="card section-card" style={{marginTop:16}}>
      <div className="section-head"><div><h3>Perfis das contas</h3><p className="section-description">Defina a função profissional e o ambiente de trabalho visível para cada conta.</p></div></div>
      <div className="profile-settings-list">{accounts.map(account => {
        const isRegistered = registeredEmails.has(account.email)
        const mockRole = Object.entries(users).find(([, user]) => user.email.toLowerCase() === account.email)?.[0] as UserKey | undefined
        const administratorLocked = account.systemRole === 'admin'
        const updateProfile = (profile: UserProfile) => isRegistered ? onUpdateRegisteredProfile(account.email, profile) : mockRole && onUpdateMockProfile(mockRole, profile)
        const updateLevel = (level: UserLevel) => isRegistered ? onUpdateRegisteredLevel(account.email, level) : mockRole && onUpdateMockLevel(mockRole, level)
        const updateAccess = (scope: UserAccessScope) => isRegistered ? onUpdateRegisteredAccessScope(account.email, scope) : mockRole && onUpdateMockAccessScope(mockRole, scope)
        return <article className={`profile-settings-row ${searchedAccountEmail === account.email ? 'search-target' : ''}`} data-account-email={account.email} key={account.email}>
          <div className="profile-settings-main">
            <div className="project-main"><UserAvatar initials={account.initials} name={account.name} picture={account.picture}/><div><b>{account.name}</b><small>{account.email}{account.lastLoginAt && ' · acesso Google registrado'}</small></div></div>
            <div className="field"><label htmlFor={`profile-${account.email}`}>PERFIL DE USUÁRIO</label><select id={`profile-${account.email}`} value={account.profile} onChange={event => updateProfile(event.target.value as UserProfile)}>{userProfileOptions.map(profile => <option key={profile}>{profile}</option>)}</select></div>
            <div className="field"><label htmlFor={`level-${account.email}`}>NÍVEL</label><select id={`level-${account.email}`} value={account.level} onChange={event => updateLevel(event.target.value as UserLevel)}>{userLevelOptions.map(level => <option key={level}>{level}</option>)}</select></div>
          </div>
          <UserAccessSettings administratorLocked={administratorLocked} clients={clients} scope={account.accessScope} onChange={updateAccess}/>
        </article>
      })}</div>
    </div>
  </> : <div className="card section-card"><div className="locked-note"><Icon name="shield"/><div><b>Perfis, clientes e operações</b><p>Essas configurações são exclusivas da conta com acesso administrativo.</p></div></div></div>}<div className="card section-card workspace-integration" style={{marginTop:16}}><div><div className="section-head"><h3>Google Workspace</h3><span className="status">Integrado</span></div><p className="subtle">Domínio autorizado: <b>@starmkt.com.br</b></p></div><div className="workspace-avatar-sync"><UserAvatar initials={googleAccount?.initials ?? 'ST'} name={googleAccount?.name} picture={googleAccount?.picture}/><div><b>{googleAccount?.name ?? 'Conta Google'}</b><small>{googleAccount?.picture ? 'Foto sincronizada com o Google' : 'Foto do perfil ainda não sincronizada'}</small></div><button type="button" className="secondary compact" disabled={avatarSyncing} onClick={onSyncGoogleAvatar}>{avatarSyncing ? 'Sincronizando…' : googleAccount?.picture ? 'Atualizar foto' : 'Sincronizar foto Google'}</button></div></div></>
}

function ClientModal({ onClose, onCreate }: { onClose: () => void; onCreate: (client: Client) => void }) {
  const [name, setName] = useState('')
  const [logo, setLogo] = useState<string>()
  const [logoProcessing, setLogoProcessing] = useState(false)
  function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); const data=new FormData(event.currentTarget); onCreate({ id: crypto.randomUUID(), name: name.trim(), logo, operations: String(data.get('operations')||'').split('\n').map(item=>item.trim()).filter(Boolean) }) }
  return <div className="modal-backdrop" onMouseDown={event=>event.target===event.currentTarget&&onClose()}><form className="modal" onSubmit={submit}><div className="modal-head"><h3>Adicionar cliente</h3><button type="button" onClick={onClose}>×</button></div><div className="field"><label>NOME DO CLIENTE</label><input name="name" value={name} onChange={event => setName(event.target.value)} required placeholder="Nome da empresa"/></div><ClientLogoField clientName={name} value={logo} onChange={setLogo} onProcessingChange={setLogoProcessing}/><div className="field"><label>OPERAÇÕES <span className="optional">OPCIONAL</span></label><textarea name="operations" rows={3} placeholder="Uma operação por linha"/><small className="field-help">As operações ficarão disponíveis no cadastro de campanhas.</small></div><div className="modal-actions"><button type="button" className="secondary" onClick={onClose}>Cancelar</button><button className="primary" disabled={logoProcessing}>{logoProcessing ? 'Processando logo…' : 'Adicionar cliente'}</button></div></form></div>
}
